import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Link,
  Typography,
  Paper,
  Chip,
  IconButton,
  TextField,
  Tabs,
  Tab,
  FormControl,
  Select,
  MenuItem,
  Collapse,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  FormControlLabel,
  Checkbox,
  useTheme,
  alpha,
  Stack,
} from '@mui/material'
import { AdminDialog } from '@/components/AdminDialog'
import { StatusChip } from 'components/StatusChip'
import type { StatusTone } from 'core/theme.tokens'
import { useIsMobile } from 'hooks/screen/useIsMobile'
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
} from '@mui/lab'
import {
  Close as CloseIcon,
  DirectionsCar,
  Assignment,
  Build,
  CheckCircle,
  Schedule,
  Note as NoteIcon,
  Payment as PaymentIcon,
  Sell as SellIcon,
  AccountBalance as AccountBalanceIcon,
  ShoppingCart as ShoppingCartIcon,
  Photo as PhotoIcon,
  Videocam as VideocamIcon,
  PictureAsPdf as PictureAsPdfIcon,
  OpenInNew as OpenInNewIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Person as PersonIcon,
  Build as BuildIcon,
  AttachFile as AttachFileIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material'
import { ref, getDownloadURL, getStorage } from '@firebase/storage'
import { Vehicle, Transporter, type Expense } from '@otw/models'
import { getTruckStatus, getShipmentPlanKind, type ShipmentPlan, type TransportTruck, type RepairTruck } from '@otw/models/logistics'
import type { VehicleTimelineNoteAttachment, VehicleTimelineNoteCategory } from '@otw/models/timeline'
import { useAuth } from '@/contexts/AuthContext'
import { useUsers } from '@/hooks/useUsers'
import { useTransporterDrivers, useTransporterGroupOwners } from '@/hooks/admin/useTransporterGroups'
import { useVehicleTimeline } from '@/hooks/timeline'
import { useExpenses } from '@/hooks/useExpenses'
import {
  getCustomerPickupSyntheticDeliveryAt,
  getPostRepairDeliveryStartAt,
  groupShipmentPlansByTruckId,
  useLogistics,
} from '@/hooks/logistics/useLogistics'
import {
  timelineShipmentRepairQueryKey,
  useTimelineShipmentRepair,
} from '@/hooks/logistics/useTimelineShipmentRepair'
import {
  fetchLegPhotosForGallery,
  useLegPhotosStatusForPlans,
} from '@/hooks/logistics/useLegPhotosStatus'
import { useSnackbar } from 'notistack'
import { useQueryClient } from '@tanstack/react-query'
import { usePickupLocations } from '@/hooks/inventory/usePickupLocations'
import { useRepairShopLocations } from '@/hooks/inventory/useRepairShopLocations'
import { ConfirmDialog } from '@/components/ConfirmDialog'
import { uploadVehicleTimelineNoteAttachments } from '@/utils/vehicleTimelineNoteUpload'
import {
  uploadRepairTruckTimelineMedia,
  type RepairPdfMediaKind,
} from '@/utils/repairTruckTimelineUpload'
import { getTrackingUrl } from '@/views/SuperAdmin/Management/TitleManager/utils/getTrackingUrl'
import MediaGalleryDialog, { type MediaGalleryItem } from './MediaGalleryDialog'
import { RepairInvoiceExpenseActions } from '../RepairInvoiceExpenseActions'
import InspectionChecklistAnswersDialog, {
  resolveCompleteChecklistId,
  resolveIntakeChecklistId,
} from './InspectionChecklistAnswersDialog'
import TimelineCustomerTab from './TimelineCustomerTab'
import TimelineAllTab from './TimelineAllTab'

const TIMELINE_TAB = {
  all: 0,
  logistics: 1,
  title: 2,
  customer: 3,
} as const

/** Stored in vehicle_timeline notes; triggers logistics + SLA delivery treatment (owners only). */
const MANUAL_TRANSPORTER_DELIVERED_NOTE_TEXT = '__OTW_MANUAL_TRANSPORTER_DELIVERED__'

function isManualTransporterDeliveredNoteText(text: string): boolean {
  return text === MANUAL_TRANSPORTER_DELIVERED_NOTE_TEXT
}

interface TimelineDialogProps {
  open: boolean
  onClose: () => void
  vehicle: Vehicle | null
}

type TitleTimelineStatus = NonNullable<NonNullable<Vehicle['title_tracking']>['title_status']>

interface TitleTimelineStep {
  status: TitleTimelineStatus
  label: string
  timestamp: Date | null
  outboundTrackingNumber?: string | null
}

type InventoryStatusDateLabel = 'Pending Sale' | 'Active Deal' | 'Available' | 'Pending Available' | 'Sold'

function parseInventoryStatusLabel(statusText: string): InventoryStatusDateLabel | null {
  const normalized = statusText.toLowerCase().trim().replace(/\s+/g, ' ')
  if (normalized === 'pending sale') return 'Pending Sale'
  if (normalized === 'active deal') return 'Active Deal'
  if (normalized === 'available') return 'Available'
  if (normalized === 'pending available') return 'Pending Available'
  if (normalized === 'sold') return 'Sold'
  return null
}

function parseTimelineStatusChangeTarget(noteText: string | undefined): InventoryStatusDateLabel | null {
  const text = (noteText ?? '').trim()
  if (!text) return null
  const match = text.match(/^inventory status changed:\s*(.+?)\s*->\s*(.+?)\./i)
  if (!match) return null
  return parseInventoryStatusLabel(match[2] ?? '')
}

function parseTimelineStatusChangeRelatedUserName(noteText: string | undefined): string | undefined {
  const text = (noteText ?? '').trim()
  if (!text) return undefined
  const match = text.match(/selling to:\s*(.+?)\.\s*$/i)
  const parsedName = match?.[1]?.trim()
  return parsedName || undefined
}

const getExpenseAttachmentUrls = (expense: Pick<Expense, 'attachmentURLs' | 'attachmentURL'>): string[] => {
  const fromArray = Array.isArray(expense.attachmentURLs)
    ? expense.attachmentURLs.filter((url): url is string => typeof url === 'string' && url.length > 0)
    : []
  if (fromArray.length > 0) return fromArray
  return expense.attachmentURL ? [expense.attachmentURL] : []
}

const formatExpenseAmount = (amount: number): string =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount)

const getExpenseCategoryLabel = (expense: Pick<Expense, 'category' | 'customCategory'>): string => {
  if (expense.category === 'Other' && expense.customCategory?.trim()) {
    return expense.customCategory.trim()
  }
  switch (expense.category) {
    case 'RentalReimbursement':
      return 'Rental Reimbursement'
    case 'StorageFees':
      return 'Storage Fees'
    default:
      return expense.category || 'Expense'
  }
}

type ShipmentRepairTimelineEntry =
  | {
      type: 'truck_status'
      key: string
      timestamp: any
      truckId: string
      status: string
      isPostDeliveryRepair?: boolean
    }
  | {
      type: 'leg_status'
      key: string
      timestamp: any
      planId: string
      legIndex: number
      status: string
      notes?: string
      fromId: string
      fromType: string
      toId: string
      toType: string
      isPostDeliveryRepair?: boolean
    }
  | {
      type: 'repair_status'
      key: string
      timestamp: any
      repairTruckId: string
      status: string
      userId?: string
      isPostDeliveryRepair?: boolean
    }
  | {
      type: 'repair_note'
      key: string
      timestamp: any
      repairTruckId: string
      note: string
      userId?: string
      userName?: string
      mediaType?: 'photo' | 'video' | 'invoice' | 'estimate' | 'damage_photo'
      mediaStoragePath?: string
      isPostDeliveryRepair?: boolean
    }
  | {
      type: 'repair_paid'
      key: string
      timestamp: any
      repairTruckId: string
      paid: boolean
      isPostDeliveryRepair?: boolean
    }
  | {
      type: 'pdi_completed'
      key: string
      timestamp: any
      repairTruckId: string
      isPostDeliveryRepair?: boolean
    }
  | {
      type: 'vehicle_note'
      key: string
      timestamp: any
      note: string
      userId?: string
      noteCategory: VehicleTimelineNoteCategory
      attachments?: VehicleTimelineNoteAttachment[]
    }
  | {
      type: 'vehicle_paid_for'
      key: string
      timestamp: { toDate: () => Date; toMillis: () => number }
    }
  | {
      type: 'vehicle_purchased_for_inventory'
      key: string
      timestamp: { toDate: () => Date; toMillis: () => number }
      whoName?: string
    }
  | {
      type: 'inventory_status_date'
      key: string
      timestamp: { toDate: () => Date; toMillis: () => number }
      statusLabel: InventoryStatusDateLabel
      relatedUserName?: string
    }
  | {
      type: 'vehicle_funded_date'
      key: string
      timestamp: { toDate: () => Date; toMillis: () => number }
    }
  | {
      type: 'vehicle_expense'
      key: string
      timestamp: { toDate: () => Date; toMillis: () => number }
      expenseId: string
      categoryLabel: string
      amount: number
      description: string
      businessName?: string
      attachmentUrls: string[]
      paid: boolean
    }
  | {
      type: 'manual_transporter_delivered'
      key: string
      timestamp: any
      userId?: string
      noteOriginalIndex: number
    }

type ShipmentRepairTimelineDisplayEntry =
  | ShipmentRepairTimelineEntry
  | {
      type: 'repair_media_group'
      key: string
      timestamp: any
      repairTruckId: string
      entries: Array<Extract<ShipmentRepairTimelineEntry, { type: 'repair_note' }>>
      photoCount: number
      videoCount: number
      isPostDeliveryRepair?: boolean
    }

type RepairMediaNoteEntry = Extract<ShipmentRepairTimelineEntry, { type: 'repair_note' }>

function RepairPdfLink({ storagePath, label }: { storagePath: string; label: 'invoice' | 'estimate' }) {
  const [url, setUrl] = useState<string | null>(null)
  const [status, setStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle')

  useEffect(() => {
    if (!storagePath) {
      setUrl(null)
      setStatus('idle')
      return
    }
    let cancelled = false
    setStatus('loading')
    getDownloadURL(ref(getStorage(), storagePath))
      .then((downloadUrl) => {
        if (cancelled) return
        setUrl(downloadUrl)
        setStatus('ready')
      })
      .catch(() => {
        if (cancelled) return
        setUrl(null)
        setStatus('error')
      })
    return () => {
      cancelled = true
    }
  }, [storagePath])

  if (status === 'loading') {
    return (
      <Typography variant="caption" color="text.secondary">
        {`Loading ${label}...`}
      </Typography>
    )
  }
  if (status === 'error' || !url) {
    return (
      <Typography variant="caption" color="error">
        {`Could not open ${label}`}
      </Typography>
    )
  }

  return (
    <Button
      component="a"
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      size="small"
      variant="outlined"
      startIcon={<PictureAsPdfIcon fontSize="small" />}
      endIcon={<OpenInNewIcon fontSize="small" />}
      sx={{ textTransform: 'none' }}
    >
      {`Open ${label}`}
    </Button>
  )
}

function buildShipmentRepairTimelineDisplayEntries(
  entries: ShipmentRepairTimelineEntry[]
): ShipmentRepairTimelineDisplayEntry[] {
  const displayEntries: ShipmentRepairTimelineDisplayEntry[] = []
  let i = 0

  while (i < entries.length) {
    const current = entries[i]
    const isRepairMediaNote =
      current.type === 'repair_note' &&
      (current.mediaType === 'photo' || current.mediaType === 'video') &&
      Boolean(current.mediaStoragePath)

    if (!isRepairMediaNote) {
      displayEntries.push(current)
      i += 1
      continue
    }

    const run: Array<Extract<ShipmentRepairTimelineEntry, { type: 'repair_note' }>> = []
    while (i < entries.length) {
      const candidate = entries[i]
      const isCandidateMediaNote =
        candidate.type === 'repair_note' &&
        candidate.repairTruckId === current.repairTruckId &&
        (candidate.mediaType === 'photo' || candidate.mediaType === 'video') &&
        Boolean(candidate.mediaStoragePath)
      if (!isCandidateMediaNote) break
      run.push(candidate as Extract<ShipmentRepairTimelineEntry, { type: 'repair_note' }>)
      i += 1
    }

    if (run.length >= 2) {
      let photoCount = 0
      let videoCount = 0
      run.forEach((entry) => {
        if (entry.mediaType === 'photo') photoCount += 1
        if (entry.mediaType === 'video') videoCount += 1
      })
      displayEntries.push({
        type: 'repair_media_group',
        key: `repair-media-group-${run[0].key}-${run[run.length - 1].key}`,
        timestamp: run[0].timestamp,
        repairTruckId: run[0].repairTruckId,
        entries: run,
        photoCount,
        videoCount,
        isPostDeliveryRepair: run[0].isPostDeliveryRepair,
      })
      continue
    }

    displayEntries.push(run[0])
  }

  return displayEntries
}

type SlaStepState =
  | 'completed_on_time'
  | 'completed_late'
  | 'in_progress'
  | 'at_risk'
  | 'overdue'
  | 'not_started'

interface SlaStep {
  key: string
  label: string
  startAt: Date | null
  endAt: Date | null
  dueAt: Date | null
  budgetMs: number
  elapsedMs: number
  progress: number
  state: SlaStepState
  helperText: string
}

const HOUR_MS = 60 * 60 * 1000
const DAY_MS = 24 * HOUR_MS

function parseDateInput(value: unknown): Date | null {
  if (!value) return null
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value
  if (typeof value === 'string') {
    const d = new Date(value)
    return isNaN(d.getTime()) ? null : d
  }
  if (typeof value === 'object' && value !== null) {
    const maybeTs = value as { toDate?: () => Date; toMillis?: () => number }
    if (typeof maybeTs.toDate === 'function') {
      const d = maybeTs.toDate()
      return isNaN(d.getTime()) ? null : d
    }
    if (typeof maybeTs.toMillis === 'function') {
      const d = new Date(maybeTs.toMillis())
      return isNaN(d.getTime()) ? null : d
    }
  }
  return null
}

function formatSlaDuration(ms: number): string {
  const safe = Math.max(0, ms)
  const days = Math.floor(safe / DAY_MS)
  return `${days}d`
}

function formatSlaDate(date: Date | null): string {
  if (!date) return 'Not set'
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function getStepStateLabel(state: SlaStepState): string {
  switch (state) {
    case 'completed_on_time':
      return 'Completed on time'
    case 'completed_late':
      return 'Completed late'
    case 'in_progress':
      return 'In progress'
    case 'at_risk':
      return 'At risk'
    case 'overdue':
      return 'Overdue'
    default:
      return 'Not started'
  }
}

function slaStepStateTone(state: SlaStepState): StatusTone {
  switch (state) {
    case 'completed_on_time':
      return 'success'
    case 'completed_late':
    case 'overdue':
      return 'danger'
    case 'at_risk':
      return 'warning'
    case 'in_progress':
      return 'info'
    default:
      return 'neutral'
  }
}

function formatStatusLabel(status: string): string {
  const normalized = status.replace(/_/g, ' ').trim()
  if (!normalized) return ''
  return normalized.charAt(0).toUpperCase() + normalized.slice(1)
}

function isDuplicateRepairSystemNote(note: string | undefined): boolean {
  const text = (note ?? '').trim().toLowerCase()
  if (!text) return false
  if (text.startsWith('work status updated to')) return true
  if (text.startsWith('work status changed:')) return true
  if (text.startsWith('paid status changed:')) return true
  if (text === 'payment completed') return true
  if (text === 'payment marked as pending') return true
  if (text === 'truck marked ready for pickup. repairs complete.') return true
  return false
}

function getEntryTimestampMs(timestamp: unknown): number {
  const value = timestamp as { toMillis?: () => number; toDate?: () => Date } | null | undefined
  return value?.toMillis?.() ?? value?.toDate?.()?.getTime?.() ?? 0
}

function findClosestLegForTimestamp(plan: ShipmentPlan | undefined, timestamp: unknown) {
  const eventTime = getEntryTimestampMs(timestamp)
  if (!plan?.shipment_legs?.length) return undefined
  let closest:
    | {
        leg: NonNullable<ShipmentPlan['shipment_legs']>[number]
        updatedAt: number
      }
    | undefined
  ;(plan.shipment_legs ?? []).forEach((leg) => {
    ;(leg.status_history ?? []).forEach((history) => {
      const updatedAt = getEntryTimestampMs(history.updated_at)
      if (updatedAt <= eventTime) {
        if (!closest || updatedAt > closest.updatedAt) {
          closest = { leg, updatedAt }
        }
      }
    })
  })
  return closest?.leg ?? plan.shipment_legs[0]
}

/** Company assigned on the leg at (or just before) the entry timestamp; falls back to current company_id. */
function getCompanyIdForLegAtTimestamp(
  leg: NonNullable<ShipmentPlan['shipment_legs']>[number] | undefined,
  timestamp: unknown
): string | undefined {
  if (!leg) return undefined
  const eventTime = getEntryTimestampMs(timestamp)
  let closest: { companyId: string; assignedAt: number } | undefined
  ;(leg.company_history ?? []).forEach((history) => {
    if (!history.company_id) return
    const assignedAt = getEntryTimestampMs(history.timestamp)
    if (assignedAt <= eventTime) {
      if (!closest || assignedAt > closest.assignedAt) {
        closest = { companyId: history.company_id, assignedAt }
      }
    }
  })
  return closest?.companyId ?? leg.company_id
}

function getShipmentLegIndex(
  plan: ShipmentPlan | undefined,
  leg: NonNullable<ShipmentPlan['shipment_legs']>[number] | undefined
): number {
  if (!plan?.shipment_legs?.length || !leg) return -1
  return plan.shipment_legs.findIndex(
    (candidate) =>
      candidate.from_id === leg.from_id &&
      candidate.to_id === leg.to_id &&
      candidate.from_type === leg.from_type &&
      candidate.to_type === leg.to_type
  )
}

function repairNoteEntriesToGalleryItems(
  entries: RepairMediaNoteEntry[],
  userIdToDisplayName: Record<string, string>,
  formatDate: (timestamp: unknown) => string
): { photos: MediaGalleryItem[]; videos: MediaGalleryItem[] } {
  const photos = entries
    .filter(
      (entry) =>
        (entry.mediaType === 'photo' || entry.mediaType === 'damage_photo') &&
        Boolean(entry.mediaStoragePath)
    )
    .map((entry) => ({
      key: entry.key,
      storagePath: entry.mediaStoragePath || '',
      mediaKind: 'photo' as const,
      noteText: (entry.note || '').trim(),
      metaText: `${entry.userId ? userIdToDisplayName[entry.userId] ?? entry.userName ?? 'Unknown' : entry.userName ?? 'Unknown'} · ${formatDate(entry.timestamp)}`,
    }))
  const videos = entries
    .filter((entry) => entry.mediaType === 'video' && Boolean(entry.mediaStoragePath))
    .map((entry) => ({
      key: entry.key,
      storagePath: entry.mediaStoragePath || '',
      mediaKind: 'video' as const,
      noteText: (entry.note || '').trim(),
      metaText: `${entry.userId ? userIdToDisplayName[entry.userId] ?? entry.userName ?? 'Unknown' : entry.userName ?? 'Unknown'} · ${formatDate(entry.timestamp)}`,
    }))
  return { photos, videos }
}

const TITLE_TIMELINE_SEQUENCE: Array<{
  status: TitleTimelineStatus
  label: string
  getTimestamp: (vehicle: Vehicle, titleTracking: Vehicle['title_tracking']) => string | null | undefined
}> = [
  {
    status: 'purchased_for_inventory',
    label: 'Purchased for inventory',
    // Uses the vehicle-level date by request.
    getTimestamp: (vehicle) => vehicle.purchased_for_inventory_date,
  },
  {
    status: 'bank_information',
    label: 'Bank information',
    // Uses bank_contacted_date by request.
    getTimestamp: (_vehicle, titleTracking) => titleTracking?.bank_contacted_date,
  },
  {
    status: 'payment_sent',
    label: 'Payment sent',
    getTimestamp: (_vehicle, titleTracking) => titleTracking?.paid_date,
  },
  {
    status: 'title_requested',
    label: 'Title requested',
    getTimestamp: (_vehicle, titleTracking) => titleTracking?.title_requested_date,
  },
  {
    status: 'in_transit',
    label: 'In transit',
    getTimestamp: (_vehicle, titleTracking) => titleTracking?.in_transit_date,
  },
  {
    status: 'physical_title_received',
    label: 'Physical title received',
    getTimestamp: (_vehicle, titleTracking) => titleTracking?.physical_title_received_date,
  },
  {
    status: 'title_flip_required',
    label: 'Title flip required',
    getTimestamp: (_vehicle, titleTracking) =>
      titleTracking?.title_flip_required === true ? titleTracking?.title_flip_required_date : null,
  },
  {
    status: 'title_flip_not_required',
    label: 'Title flip not required',
    getTimestamp: (_vehicle, titleTracking) =>
      titleTracking?.title_flip_required === false ? titleTracking?.title_flip_required_date : null,
  },
  {
    status: 'title_flip_in_progress',
    label: 'Title flip in progress',
    getTimestamp: (_vehicle, titleTracking) => titleTracking?.title_flip_in_progress_date,
  },
  {
    status: 'title_flip_completed',
    label: 'Title flip completed',
    getTimestamp: (_vehicle, titleTracking) => titleTracking?.title_flip_completed_date,
  },
  {
    status: 'shipping_information',
    label: 'Shipping information',
    getTimestamp: (_vehicle, titleTracking) => titleTracking?.shipping_info_completed_date,
  },
  {
    status: 'title_shipped',
    label: 'Title shipped',
    getTimestamp: (_vehicle, titleTracking) => titleTracking?.title_shipped_date,
  },
  {
    status: 'completed',
    label: 'Completed',
    getTimestamp: (_vehicle, titleTracking) => titleTracking?.completed_date,
  },
]

function timelineTimestampToMs(value: unknown): number {
  if (!value) return 0
  if (typeof (value as { toMillis?: () => number }).toMillis === 'function') {
    return (value as { toMillis: () => number }).toMillis()
  }
  if (typeof (value as { seconds?: number }).seconds === 'number') {
    return ((value as { seconds: number }).seconds ?? 0) * 1000
  }
  const parsed = new Date(value as string | number | Date).getTime()
  return Number.isNaN(parsed) ? 0 : parsed
}

/** True when `atMs` falls in a post-delivery repair plan window for this truck (until the next plan). */
function isTimestampInPostDeliveryPlanWindow(
  truckId: string,
  atMs: number,
  plans: ShipmentPlan[],
): boolean {
  if (!truckId || atMs <= 0) return false
  const truckPlans = plans
    .filter((plan) => plan.transport_truck_id === truckId)
    .slice()
    .sort((a, b) => timelineTimestampToMs(a.created_at) - timelineTimestampToMs(b.created_at))

  for (let i = 0; i < truckPlans.length; i++) {
    const plan = truckPlans[i]
    if (getShipmentPlanKind(plan) !== 'post_delivery_repair') continue
    const startMs = timelineTimestampToMs(plan.created_at)
    const endMs =
      i + 1 < truckPlans.length
        ? timelineTimestampToMs(truckPlans[i + 1].created_at)
        : Number.POSITIVE_INFINITY
    if (atMs >= startMs && atMs < endMs) return true
  }
  return false
}

const TimelineDialog: React.FC<TimelineDialogProps> = ({ open, onClose, vehicle }) => {
  const theme = useTheme()
  const isMobile = useIsMobile()
  const { enqueueSnackbar } = useSnackbar()
  const { userInfo } = useAuth()
  const { users } = useUsers()
  const { vehicleTimeline, addNote, deleteNote } = useVehicleTimeline(vehicle?.id)
  const { expenses: vehicleExpenses } = useExpenses({
    vin: vehicle?.id,
    options: { dontShowUpdates: true },
  })
  const { addRepairTruckNote, isAddingRepairTruckNote } = useLogistics()
  const queryClient = useQueryClient()

  const timelineNotes = useMemo(() => {
    const notes = vehicleTimeline?.notes ?? []
    return notes
      .map((note, i) => ({ note, originalIndex: i }))
      .sort((a, b) => {
        const tA = a.note.created_at?.toDate?.()?.getTime?.() ?? (a.note.created_at as { toMillis?: () => number })?.toMillis?.() ?? 0
        const tB = b.note.created_at?.toDate?.()?.getTime?.() ?? (b.note.created_at as { toMillis?: () => number })?.toMillis?.() ?? 0
        return tA - tB
      })
  }, [vehicleTimeline?.notes])

  const [addNoteDialogOpen, setAddNoteDialogOpen] = useState(false)
  const [newNoteText, setNewNoteText] = useState('')
  const [newNoteKind, setNewNoteKind] = useState<'generic' | 'repair'>('generic')
  const [newNoteFiles, setNewNoteFiles] = useState<File[]>([])
  const [newNoteRepairPdfKind, setNewNoteRepairPdfKind] = useState<RepairPdfMediaKind>('invoice')
  const [newNoteMarkImagesAsDamage, setNewNoteMarkImagesAsDamage] = useState(false)
  const addNoteFileInputRef = useRef<HTMLInputElement>(null)
  const [isSavingNote, setIsSavingNote] = useState(false)
  const [markDeliveredConfirmOpen, setMarkDeliveredConfirmOpen] = useState(false)
  const [unmarkDeliveredConfirmOpen, setUnmarkDeliveredConfirmOpen] = useState(false)
  const [activeTab, setActiveTab] = useState<number>(TIMELINE_TAB.all)

  useEffect(() => {
    if (open) setActiveTab(TIMELINE_TAB.all)
  }, [open, vehicle?.id])
  const [expandedLogisticsEntries, setExpandedLogisticsEntries] = useState<Record<string, boolean>>({})
  const [mediaGalleryOpen, setMediaGalleryOpen] = useState<{
    photos: MediaGalleryItem[]
    videos: MediaGalleryItem[]
  } | null>(null)
  const [loadingLegPhotosKey, setLoadingLegPhotosKey] = useState<string | null>(null)
  const [inspectionChecklistViewRepairTruck, setInspectionChecklistViewRepairTruck] = useState<RepairTruck | null>(null)
  const { pickupLocations } = usePickupLocations()
  const { repairShopLocations } = useRepairShopLocations()

  const { data: shipmentRepairData } = useTimelineShipmentRepair(vehicle?.id, {
    enabled: open && Boolean(vehicle?.id),
  })

  const shipmentPlanIds = useMemo(
    () => [...new Set((shipmentRepairData?.plans ?? []).map((plan) => plan.id).filter(Boolean))],
    [shipmentRepairData?.plans]
  )
  const legPhotosStatusByPlanId = useLegPhotosStatusForPlans(
    shipmentPlanIds,
    open && shipmentPlanIds.length > 0
  )

  const transportSla = useMemo(() => {
    const now = new Date()
    const paidForAt = parseDateInput(vehicle?.paid_for_date)
    const fundedAt = parseDateInput(vehicle?.funded_date)

    const truckCreatedAt = (shipmentRepairData?.trucks ?? [])
      .map((truck) => parseDateInput(truck.created_at))
      .filter((date): date is Date => Boolean(date))
      .sort((a, b) => a.getTime() - b.getTime())[0] ?? null

    const allTruckStatusEvents: Array<{ status: string; enteredAt: Date }> = (shipmentRepairData?.trucks ?? [])
      .flatMap((truck) =>
        (truck.status_history ?? []).map((historyEntry) => ({
          status: historyEntry.status as string,
          enteredAt: parseDateInput(historyEntry.entered_at),
        }))
      )
      .filter((entry): entry is { status: string; enteredAt: Date } => entry.enteredAt !== null)
      .sort((a, b) => a.enteredAt.getTime() - b.enteredAt.getTime())

    const repairShopAt = allTruckStatusEvents.find((event) => event.status === 'repair')?.enteredAt ?? null
    const pickedUpFromRepairAt =
      allTruckStatusEvents.find(
        (event) =>
          event.status === 'in_transit' &&
          (!repairShopAt || event.enteredAt.getTime() >= repairShopAt.getTime())
      )?.enteredAt ?? null
    const deliveredFromTruck =
      allTruckStatusEvents.find(
        (event) =>
          event.status === 'delivered' &&
          (!pickedUpFromRepairAt || event.enteredAt.getTime() >= pickedUpFromRepairAt.getTime())
      )?.enteredAt ?? null

    let manualDeliveredAt: Date | null = null
    for (const note of vehicleTimeline?.notes ?? []) {
      if (isManualTransporterDeliveredNoteText(note.text)) {
        const parsed = parseDateInput(note.created_at)
        if (parsed) {
          manualDeliveredAt = parsed
          break
        }
      }
    }

    const customerPickupDeliveredAt = (() => {
      const trucks = shipmentRepairData?.trucks ?? []
      const plansByTruckId = groupShipmentPlansByTruckId(shipmentRepairData?.plans ?? [])
      const candidates = trucks
        .map((truck) =>
          getCustomerPickupSyntheticDeliveryAt(truck, plansByTruckId.get(truck.id) ?? [])
        )
        .filter((date): date is Date => Boolean(date))
      if (candidates.length === 0) return null
      return new Date(Math.max(...candidates.map((date) => date.getTime())))
    })()

    const deliveredToCustomerAt = (() => {
      const candidates = [deliveredFromTruck, manualDeliveredAt, customerPickupDeliveredAt].filter(
        (d): d is Date => Boolean(d)
      )
      if (candidates.length === 0) return null
      return new Date(Math.max(...candidates.map((d) => d.getTime())))
    })()

    const postRepairDeliveryStartAt = getPostRepairDeliveryStartAt(
      shipmentRepairData?.trucks ?? [],
      shipmentRepairData?.plans ?? [],
      repairShopAt
    )

    const createStep = (
      key: string,
      label: string,
      startAt: Date | null,
      endAt: Date | null,
      budgetMs: number
    ): SlaStep => {
      if (!startAt || budgetMs <= 0) {
        return {
          key,
          label,
          startAt,
          endAt,
          dueAt: startAt && budgetMs > 0 ? new Date(startAt.getTime() + budgetMs) : null,
          budgetMs,
          elapsedMs: 0,
          progress: 0,
          state: 'not_started',
          helperText: 'Waiting for start event',
        }
      }

      const dueAt = new Date(startAt.getTime() + budgetMs)
      const isCompleted = Boolean(endAt)
      const elapsedMs = Math.max(0, (endAt ?? now).getTime() - startAt.getTime())
      const progress = Math.max(0, Math.min(1, elapsedMs / budgetMs))
      const overdue = elapsedMs > budgetMs
      const atRisk = !isCompleted && progress >= 0.8
      const state: SlaStepState = isCompleted
        ? overdue
          ? 'completed_late'
          : 'completed_on_time'
        : overdue
          ? 'overdue'
          : atRisk
            ? 'at_risk'
            : 'in_progress'

      const helperText = isCompleted
        ? overdue
          ? `${formatSlaDuration(elapsedMs - budgetMs)} late`
          : `${formatSlaDuration(budgetMs - elapsedMs)} early`
        : overdue
          ? `${formatSlaDuration(elapsedMs - budgetMs)} overdue`
          : `${formatSlaDuration(budgetMs - elapsedMs)} remaining`

      return {
        key,
        label,
        startAt,
        endAt,
        dueAt,
        budgetMs,
        elapsedMs,
        progress,
        state,
        helperText,
      }
    }

    // Funded delivery SLA: 21 days total, but credit the 7-day transit-to-repair
    // and 7-day repair buckets if they were already completed before funding.
    let fundedBudgetDays = 21
    if (fundedAt && repairShopAt && repairShopAt.getTime() <= fundedAt.getTime()) fundedBudgetDays -= 7
    if (
      fundedAt &&
      pickedUpFromRepairAt &&
      pickedUpFromRepairAt.getTime() <= fundedAt.getTime()
    ) {
      fundedBudgetDays -= 7
    }
    fundedBudgetDays = Math.max(7, fundedBudgetDays)

    const steps: SlaStep[] = [
      createStep(
        'paid_to_transport',
        'Paid -> Added to transport',
        paidForAt,
        truckCreatedAt,
        24 * HOUR_MS
      ),
      createStep(
        'transport_to_repair',
        'Added to transport -> At repair shop',
        truckCreatedAt,
        repairShopAt,
        5 * DAY_MS
      ),
      createStep(
        'repair_cycle',
        'At repair shop -> Picked up',
        repairShopAt,
        pickedUpFromRepairAt,
        7 * DAY_MS
      ),
      createStep(
        'post_repair_delivery',
        'Repairs done -> Delivered to customer',
        postRepairDeliveryStartAt,
        deliveredToCustomerAt,
        7 * DAY_MS
      ),
      createStep(
        'funded_to_delivery',
        'Funded -> Delivered',
        fundedAt,
        deliveredToCustomerAt,
        fundedBudgetDays * DAY_MS
      ),
    ]

    return {
      steps,
      hasAnchorEvent: Boolean(paidForAt || fundedAt || truckCreatedAt || repairShopAt || deliveredToCustomerAt),
      fundedBudgetDays,
    }
  }, [
    shipmentRepairData?.trucks,
    shipmentRepairData?.plans,
    vehicle?.funded_date,
    vehicle?.paid_for_date,
    vehicleTimeline?.notes,
  ])

  const userIdToDisplayName = useMemo(() => {
    const map: Record<string, string> = {}
    if (userInfo?.id) {
      const currentName = [userInfo.first_name, userInfo.last_name].filter(Boolean).join(' ').trim()
      map[userInfo.id] = currentName || userInfo.email || 'Unknown'
    }
    users.forEach(u => {
      const name = [u.first_name, u.last_name].filter(Boolean).join(' ').trim()
      map[u.id] = map[u.id] ?? (name || u.email || 'Unknown')
    })
    return map
  }, [users, userInfo])

  const transporterIds = useMemo(() => {
    const ids: string[] = []
    ;(shipmentRepairData?.plans ?? []).forEach((plan) => {
      ;(plan.shipment_legs ?? []).forEach((leg) => {
        if (leg.driver_id) ids.push(leg.driver_id)
      })
    })
    return [...new Set(ids)]
  }, [shipmentRepairData?.plans])

  // Fetch transporter information for assignment entries
  const { data: transportersData } = useTransporterDrivers(transporterIds)
  const transportersMap: { [key: string]: Transporter } = transportersData || {}

  const manualDeliveredNoteOriginalIndex = useMemo(
    () => vehicleTimeline?.notes?.findIndex((n) => isManualTransporterDeliveredNoteText(n.text)) ?? -1,
    [vehicleTimeline?.notes]
  )

  // Early return after all hooks
  if (!vehicle) {
    return null
  }

  const handleAddNoteClick = () => {
    setNewNoteText('')
    setNewNoteKind('generic')
    setNewNoteFiles([])
    setNewNoteRepairPdfKind('invoice')
    setNewNoteMarkImagesAsDamage(false)
    setAddNoteDialogOpen(true)
  }

  const resetAddNoteDialogState = () => {
    setNewNoteText('')
    setNewNoteKind('generic')
    setNewNoteFiles([])
    setNewNoteRepairPdfKind('invoice')
    setNewNoteMarkImagesAsDamage(false)
  }

  const handleSaveNote = async () => {
    const text = newNoteText.trim()

    if (!vehicle) {
      enqueueSnackbar('Vehicle is not known', { variant: 'warning' })
      return
    }
    if (!text && newNoteFiles.length === 0) {
      enqueueSnackbar('Enter a note or add at least one attachment', { variant: 'warning' })
      return
    }
    if (text && isManualTransporterDeliveredNoteText(text)) {
      enqueueSnackbar('Use Mark delivered to record a delivery milestone.', { variant: 'warning' })
      return
    }
    if (newNoteKind === 'repair' && !activeRepairTruck) {
      enqueueSnackbar('Truck is not currently at a repair shop.', { variant: 'warning' })
      return
    }
    setIsSavingNote(true)
    try {
      if (newNoteKind === 'repair' && activeRepairTruck) {
        // useLogistics fires its own success/error snackbars.
        if (newNoteFiles.length > 0) {
          const mediaItems = await uploadRepairTruckTimelineMedia(
            activeRepairTruck.id,
            newNoteFiles,
            {
              pdfKind: newNoteRepairPdfKind,
              markImagesAsDamage: newNoteMarkImagesAsDamage,
            }
          )
          const suppressToast = mediaItems.length > 1 || (mediaItems.length === 1 && !!text)
          for (const media of mediaItems) {
            await addRepairTruckNote({
              repairTruckId: activeRepairTruck.id,
              note: text,
              media,
              suppressSuccessToast: suppressToast,
            })
          }
          if (suppressToast) {
            enqueueSnackbar(
              mediaItems.length === 1
                ? 'Repair media added'
                : `${mediaItems.length} repair media items added`,
              { variant: 'success' }
            )
          }
        } else {
          await addRepairTruckNote({
            repairTruckId: activeRepairTruck.id,
            note: text,
          })
        }
        // useLogistics only invalidates ['repair_trucks']; this dialog uses its own query.
        await queryClient.invalidateQueries({ queryKey: timelineShipmentRepairQueryKey(vehicle?.id) })
      } else {
        let attachments: VehicleTimelineNoteAttachment[] | undefined
        if (newNoteFiles.length > 0) {
          attachments = await uploadVehicleTimelineNoteAttachments(vehicle.id, newNoteFiles)
        }
        await addNote.mutateAsync({
          text: text || '',
          ...(userInfo?.id && { created_by_user_id: userInfo.id }),
          category: 'generic',
          ...(attachments && attachments.length > 0 && { attachments }),
        })
        enqueueSnackbar('Note added', { variant: 'success' })
      }
      setAddNoteDialogOpen(false)
      resetAddNoteDialogState()
    } catch (error) {
      console.error('Error adding note:', error)
      if (newNoteKind !== 'repair') {
        enqueueSnackbar('Failed to add note', { variant: 'error' })
      } else if (error instanceof Error && error.message.startsWith('Unsupported file type')) {
        enqueueSnackbar(error.message, { variant: 'warning' })
      }
    } finally {
      setIsSavingNote(false)
    }
  }

  const handleConfirmMarkDelivered = async () => {
    setMarkDeliveredConfirmOpen(false)
    if (!vehicle?.id) return
    try {
      await addNote.mutateAsync({
        text: MANUAL_TRANSPORTER_DELIVERED_NOTE_TEXT,
        ...(userInfo?.id && { created_by_user_id: userInfo.id }),
      })
      enqueueSnackbar('Marked as delivered to customer', { variant: 'success' })
    } catch (error) {
      console.error('Error marking delivered:', error)
      enqueueSnackbar('Failed to mark delivered', { variant: 'error' })
    }
  }

  const handleConfirmUnmarkDelivered = async () => {
    setUnmarkDeliveredConfirmOpen(false)
    if (manualDeliveredNoteOriginalIndex < 0 || !vehicle?.id) return
    try {
      await deleteNote.mutateAsync({ noteIndex: manualDeliveredNoteOriginalIndex })
      enqueueSnackbar('Delivery mark removed', { variant: 'success' })
    } catch (error) {
      console.error('Error removing delivery mark:', error)
      enqueueSnackbar('Failed to remove delivery mark', { variant: 'error' })
    }
  }

  const toggleLogisticsEntryExpanded = (entryKey: string) => {
    setExpandedLogisticsEntries((prev) => ({
      ...prev,
      [entryKey]: !prev[entryKey],
    }))
  }

  const formatDate = (timestamp: any) => {
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp.toMillis())
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const getTimeAgo = (timestamp: any) => {
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp.toMillis())
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    const diffMinutes = Math.floor(diffMs / (1000 * 60))

    if (diffDays > 0) {
      return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`
    } else if (diffHours > 0) {
      return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`
    } else {
      return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`
    }
  }

  const shipmentRepairEntries = useMemo((): ShipmentRepairTimelineEntry[] => {
    const entries: ShipmentRepairTimelineEntry[] = []
    const plans = shipmentRepairData?.plans ?? []

    ;(shipmentRepairData?.trucks ?? []).forEach((truck) => {
      ;(truck.status_history ?? []).forEach((historyEntry, historyIndex) => {
        const atMs = timelineTimestampToMs(historyEntry.entered_at)
        entries.push({
          type: 'truck_status',
          key: `truck-status-${truck.id}-${historyIndex}`,
          timestamp: historyEntry.entered_at,
          truckId: truck.id,
          status: historyEntry.status,
          isPostDeliveryRepair: isTimestampInPostDeliveryPlanWindow(truck.id, atMs, plans),
        })
      })
    })

    plans.forEach((plan) => {
      const isPostDeliveryPlan = getShipmentPlanKind(plan) === 'post_delivery_repair'
      ;(plan.shipment_legs ?? []).forEach((leg, legIndex) => {
        ;(leg.status_history ?? []).forEach((historyEntry, historyIndex) => {
          entries.push({
            type: 'leg_status',
            key: `leg-${plan.id}-${legIndex}-${historyIndex}`,
            timestamp: historyEntry.updated_at,
            planId: plan.id,
            legIndex,
            status: historyEntry.status,
            notes: historyEntry.notes,
            fromId: leg.from_id,
            fromType: leg.from_type,
            toId: leg.to_id,
            toType: leg.to_type,
            isPostDeliveryRepair: isPostDeliveryPlan,
          })
        })
      })
    })

    ;(shipmentRepairData?.repairTrucks ?? []).forEach((repairTruck) => {
      const isPostDeliveryRepair = isTimestampInPostDeliveryPlanWindow(
        repairTruck.transport_truck_id,
        timelineTimestampToMs(repairTruck.created_at),
        plans,
      )
      ;(repairTruck.work_status_history ?? []).forEach((statusEntry, statusIndex) => {
        entries.push({
          type: 'repair_status',
          key: `repair-status-${repairTruck.id}-${statusIndex}`,
          timestamp: statusEntry.timestamp,
          repairTruckId: repairTruck.id,
          status: statusEntry.status,
          userId: statusEntry.user_id,
          isPostDeliveryRepair,
        })
      })

      ;(repairTruck.work_notes ?? []).forEach((noteEntry, noteIndex) => {
        if (isDuplicateRepairSystemNote(noteEntry.note)) return
        entries.push({
          type: 'repair_note',
          key: `repair-note-${repairTruck.id}-${noteIndex}`,
          timestamp: noteEntry.timestamp,
          repairTruckId: repairTruck.id,
          note: noteEntry.note,
          userId: noteEntry.user_id,
          userName: noteEntry.user_name,
          mediaType: noteEntry.media?.type,
          mediaStoragePath: noteEntry.media?.storage_path,
          isPostDeliveryRepair,
        })
      })

      if (repairTruck.paid_date) {
        entries.push({
          type: 'repair_paid',
          key: `repair-paid-${repairTruck.id}`,
          timestamp: repairTruck.paid_date,
          repairTruckId: repairTruck.id,
          paid: repairTruck.paid,
          isPostDeliveryRepair,
        })
      }

      const pdiCompletedAt = repairTruck.inspection_checklist?.completed_at
      if (pdiCompletedAt) {
        entries.push({
          type: 'pdi_completed',
          key: `pdi-completed-${repairTruck.id}`,
          timestamp: pdiCompletedAt,
          repairTruckId: repairTruck.id,
          isPostDeliveryRepair,
        })
      }
    })

    timelineNotes.forEach(({ note, originalIndex }, index) => {
      if (isManualTransporterDeliveredNoteText(note.text)) {
        entries.push({
          type: 'manual_transporter_delivered',
          key: `manual-delivered-logistics-${originalIndex}`,
          timestamp: note.created_at,
          userId: note.created_by_user_id,
          noteOriginalIndex: originalIndex,
        })
        return
      }

      const statusLabel = parseTimelineStatusChangeTarget(note.text)
      if (statusLabel) {
        entries.push({
          type: 'inventory_status_date',
          key: `inventory-status-note-${originalIndex}`,
          timestamp: note.created_at,
          statusLabel,
          relatedUserName:
            parseTimelineStatusChangeRelatedUserName(note.text) ??
            (statusLabel === 'Sold' ? vehicle?.sold_to_user?.name : undefined),
        })
        return
      }

      entries.push({
        type: 'vehicle_note',
        key: `vehicle-note-${index}`,
        timestamp: note.created_at,
        note: note.text,
        userId: note.created_by_user_id,
        noteCategory: note.category ?? 'generic',
        ...(note.attachments && note.attachments.length > 0 ? { attachments: note.attachments } : {}),
      })
    })

    if (vehicle?.paid_for_date) {
      const d = new Date(vehicle.paid_for_date)
      if (!isNaN(d.getTime())) {
        entries.push({
          type: 'vehicle_paid_for',
          key: 'vehicle-paid-for',
          timestamp: { toDate: () => d, toMillis: () => d.getTime() },
        })
      }
    }

    if (vehicle?.purchased_for_inventory_date) {
      const d = new Date(vehicle.purchased_for_inventory_date)
      if (!isNaN(d.getTime())) {
        entries.push({
          type: 'vehicle_purchased_for_inventory',
          key: 'vehicle-purchased-for-inventory-date',
          timestamp: { toDate: () => d, toMillis: () => d.getTime() },
          whoName: vehicle.who ?? undefined,
        })
      }
    }

    if (vehicle?.funded_date) {
      const d = new Date(vehicle.funded_date)
      if (!isNaN(d.getTime())) {
        entries.push({
          type: 'vehicle_funded_date',
          key: 'vehicle-funded-date',
          timestamp: { toDate: () => d, toMillis: () => d.getTime() },
        })
      }
    }

    vehicleExpenses.forEach((expense) => {
      // Skip imports still being processed; they aren't ready to show yet.
      if (expense.reviewStatus === 'processing') return
      const expenseDate = expense.date instanceof Date ? expense.date : new Date(expense.date)
      if (isNaN(expenseDate.getTime())) return
      entries.push({
        type: 'vehicle_expense',
        key: `vehicle-expense-${expense.id}`,
        timestamp: { toDate: () => expenseDate, toMillis: () => expenseDate.getTime() },
        expenseId: expense.id,
        categoryLabel: getExpenseCategoryLabel(expense),
        amount: expense.amount,
        description: expense.description?.trim() || '',
        businessName: expense.businessName?.trim() || undefined,
        attachmentUrls: getExpenseAttachmentUrls(expense),
        paid: expense.paid === true,
      })
    })

    const statusesCoveredByTimelineNotes = new Set<InventoryStatusDateLabel>()
    timelineNotes.forEach(({ note }) => {
      const targetStatus = parseTimelineStatusChangeTarget(note.text)
      if (targetStatus) statusesCoveredByTimelineNotes.add(targetStatus)
    })

    const inventoryStatusDateEntries: Array<{
      key: string
      dateValue?: string | null
      statusLabel: InventoryStatusDateLabel
      relatedUserName?: string
    }> = [
      {
        key: 'vehicle-pending-sale-date',
        dateValue: vehicle?.pending_sale_date,
        statusLabel: 'Pending Sale',
        relatedUserName: vehicle?.selling_to_user?.name,
      },
      {
        key: 'vehicle-active-deal-date',
        dateValue: vehicle?.active_deal_date,
        statusLabel: 'Active Deal',
        relatedUserName: vehicle?.selling_to_user?.name,
      },
      {
        key: 'vehicle-pending-available-date',
        dateValue: vehicle?.pending_available_date,
        statusLabel: 'Pending Available',
      },
      {
        key: 'vehicle-available-date',
        dateValue: vehicle?.available_date,
        statusLabel: 'Available',
      },
      {
        key: 'vehicle-sold-date',
        dateValue: vehicle?.sold_date,
        statusLabel: 'Sold',
        relatedUserName: vehicle?.sold_to_user?.name,
      },
    ]
    inventoryStatusDateEntries.forEach((statusEntry) => {
      if (statusesCoveredByTimelineNotes.has(statusEntry.statusLabel)) return
      if (!statusEntry.dateValue) return
      const d = new Date(statusEntry.dateValue)
      if (isNaN(d.getTime())) return
      entries.push({
        type: 'inventory_status_date',
        key: statusEntry.key,
        timestamp: { toDate: () => d, toMillis: () => d.getTime() },
        statusLabel: statusEntry.statusLabel,
        relatedUserName: statusEntry.relatedUserName,
      })
    })

    entries.sort((a, b) => {
      const tA = a.timestamp?.toMillis?.() ?? a.timestamp?.toDate?.()?.getTime?.() ?? 0
      const tB = b.timestamp?.toMillis?.() ?? b.timestamp?.toDate?.()?.getTime?.() ?? 0
      return tA - tB
    })

    return entries
  }, [
    shipmentRepairData?.trucks,
    shipmentRepairData?.plans,
    shipmentRepairData?.repairTrucks,
    timelineNotes,
    vehicle?.paid_for_date,
    vehicle?.purchased_for_inventory_date,
    vehicle?.funded_date,
    vehicle?.sold_date,
    vehicle?.sold_to_user?.name,
    vehicle?.who,
    vehicle?.pending_sale_date,
    vehicle?.active_deal_date,
    vehicle?.pending_available_date,
    vehicle?.available_date,
    vehicle?.status,
    vehicle?.date_available,
    vehicle?.selling_to_user?.name,
    vehicleExpenses,
  ])

  const logisticsTimelineEntries = useMemo(
    () =>
      shipmentRepairEntries.filter(
        (entry) => entry.type !== 'leg_status' || Boolean(entry.isPostDeliveryRepair),
      ),
    [shipmentRepairEntries]
  )
  const logisticsTimelineDisplayEntries = useMemo(
    () => buildShipmentRepairTimelineDisplayEntries(logisticsTimelineEntries),
    [logisticsTimelineEntries]
  )

  const repairTruckById = useMemo(() => {
    const map = new Map<string, { repair_shop_location_id: string }>()
    for (const repairTruck of shipmentRepairData?.repairTrucks ?? []) {
      map.set(repairTruck.id, repairTruck)
    }
    return map
  }, [shipmentRepairData?.repairTrucks])

  const openRepairMediaGallery = (entries: RepairMediaNoteEntry[]) => {
    setMediaGalleryOpen(repairNoteEntriesToGalleryItems(entries, userIdToDisplayName, formatDate))
  }

  const handleViewLegPhotos = async (
    entryKey: string,
    planId: string,
    legIndex: number,
    flow: 'preDelivery' | 'postDelivery'
  ) => {
    setLoadingLegPhotosKey(entryKey)
    try {
      const gallery = await fetchLegPhotosForGallery(planId, legIndex, flow)
      setMediaGalleryOpen(gallery)
    } catch (error) {
      console.error('Error loading leg photos:', error)
      enqueueSnackbar('Failed to load leg photos', { variant: 'error' })
    } finally {
      setLoadingLegPhotosKey(null)
    }
  }

  const pickupLocationById = useMemo(() => {
    const map = new Map<string, { label: string; contactName?: string }>()
    ;(pickupLocations ?? []).forEach((location) => {
      if (!location.id) return
      const contactName = location.contact_name
      const label =
        location.contact_name ||
        [location.address, location.city, location.state].filter(Boolean).join(', ') ||
        location.id
      map.set(location.id, { label, contactName })
    })
    return map
  }, [pickupLocations])

  const repairLocationById = useMemo(() => {
    const map = new Map<string, { label: string; contactName?: string }>()
    ;(repairShopLocations ?? []).forEach((location) => {
      if (!location.id) return
      const contactName = location.contact_name || location.name
      const label =
        location.name ||
        location.contact_name ||
        [location.address, location.city, location.state].filter(Boolean).join(', ') ||
        location.id
      map.set(location.id, { label, contactName })
    })
    return map
  }, [repairShopLocations])

  /** Same as RepairTrackingView: custom checklist docs or per-shop defaults */
  const inspectionChecklistDialogIds = useMemo(() => {
    if (!inspectionChecklistViewRepairTruck) {
      return {
        completeChecklistId: resolveCompleteChecklistId(null),
        intakeChecklistId: resolveIntakeChecklistId(null),
      }
    }
    const shopId = inspectionChecklistViewRepairTruck.repair_shop_location_id
    const shop = (repairShopLocations ?? []).find((l) => l.id === shopId)
    return {
      completeChecklistId: resolveCompleteChecklistId(
        shop
          ? { id: shopId, inspection_checklist_id: shop.inspection_checklist_id }
          : shopId
            ? { id: shopId }
            : null,
      ),
      intakeChecklistId: resolveIntakeChecklistId(
        shop
          ? { id: shopId, intake_checklist_id: shop.intake_checklist_id }
          : shopId
            ? { id: shopId }
            : null,
      ),
    }
  }, [inspectionChecklistViewRepairTruck, repairShopLocations])
  const shipmentPlanByTruckId = useMemo(() => {
    const map = new Map<string, ShipmentPlan>()
    ;(shipmentRepairData?.plans ?? []).forEach((plan) => {
      const existing = map.get(plan.transport_truck_id)
      if (!existing) {
        map.set(plan.transport_truck_id, plan)
        return
      }
      const existingAt = parseDateInput(existing.created_at)?.getTime() ?? 0
      const currentAt = parseDateInput(plan.created_at)?.getTime() ?? 0
      if (currentAt < existingAt) {
        map.set(plan.transport_truck_id, plan)
      }
    })
    return map
  }, [shipmentRepairData?.plans])

  const repairTruckByTransportTruckId = useMemo(() => {
    const map = new Map<string, RepairTruck>()
    ;(shipmentRepairData?.repairTrucks ?? []).forEach((repairTruck) => {
      const existing = map.get(repairTruck.transport_truck_id)
      if (!existing) {
        map.set(repairTruck.transport_truck_id, repairTruck)
        return
      }
      const existingAt = parseDateInput(existing.created_at)?.getTime() ?? 0
      const currentAt = parseDateInput(repairTruck.created_at)?.getTime() ?? 0
      if (currentAt > existingAt) {
        map.set(repairTruck.transport_truck_id, repairTruck)
      }
    })
    return map
  }, [shipmentRepairData?.repairTrucks])

  /** Repair truck for the transport truck whose latest status is `repair`; null otherwise. */
  const activeRepairTruck = useMemo<RepairTruck | null>(() => {
    const trucks = shipmentRepairData?.trucks ?? []
    const candidates = trucks
      .filter((truck) => getTruckStatus(truck) === 'repair')
      .map((truck) => ({
        truck,
        repairTruck: repairTruckByTransportTruckId.get(truck.id),
        createdAtMs: parseDateInput(truck.created_at)?.getTime() ?? 0,
      }))
      .filter((c): c is { truck: TransportTruck; repairTruck: RepairTruck; createdAtMs: number } =>
        Boolean(c.repairTruck)
      )
    if (candidates.length === 0) return null
    candidates.sort((a, b) => b.createdAtMs - a.createdAtMs)
    return candidates[0].repairTruck
  }, [shipmentRepairData?.trucks, repairTruckByTransportTruckId])
  const isAtRepairShop = Boolean(activeRepairTruck)

  const shipmentCompanyGroupIds = useMemo(() => {
    const ids = new Set<string>()
    ;(shipmentRepairData?.plans ?? []).forEach((plan) => {
      ;(plan.shipment_legs ?? []).forEach((leg) => {
        if (leg.company_id) ids.add(leg.company_id)
        ;(leg.company_history ?? []).forEach((history) => {
          if (history.company_id) ids.add(history.company_id)
        })
      })
    })
    return Array.from(ids)
  }, [shipmentRepairData?.plans])

  const { data: transporterGroupOwnersData } = useTransporterGroupOwners(shipmentCompanyGroupIds)

  const shipmentRepairTimelineContent = (
    <>
      {logisticsTimelineDisplayEntries.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
            No logistics timeline entries yet.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Logistics status updates, key vehicle dates, and timeline notes will appear here.
          </Typography>
        </Box>
      ) : (
        <Timeline position="right">
          {logisticsTimelineDisplayEntries.map((entry, entryIndex) => {
            const isVehicleSideEntry =
              entry.type === 'vehicle_note' ||
              entry.type === 'vehicle_paid_for' ||
              entry.type === 'vehicle_purchased_for_inventory' ||
              entry.type === 'inventory_status_date' ||
              entry.type === 'vehicle_funded_date' ||
              entry.type === 'vehicle_expense'

            if (entry.type === 'vehicle_note') {
              const isCustomerNote = entry.noteCategory === 'customer'
              const noteDotColor: 'grey' | 'secondary' = isCustomerNote ? 'secondary' : 'grey'
              const noteIcon = isCustomerNote ? (
                <PersonIcon color="secondary" fontSize="small" />
              ) : (
                <NoteIcon color="action" fontSize="small" />
              )
              const noteHeading = isCustomerNote ? 'Customer note' : 'Note'
              const notePaperSx = isCustomerNote
                ? {
                    p: 1.5,
                    borderRadius: 2,
                    maxWidth: 320,
                    border: '1px solid',
                    borderColor: 'secondary.main',
                    bgcolor:
                      theme.palette.mode === 'dark'
                        ? 'rgba(156, 39, 176, 0.16)'
                        : 'rgba(156, 39, 176, 0.06)',
                    borderLeft: '4px solid',
                    borderLeftColor: 'secondary.main',
                  }
                : {
                    p: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    maxWidth: 320,
                  }
              return (
                <TimelineItem key={entry.key}>
                  <TimelineOppositeContent sx={{ m: 'auto 0' }} align="right" variant="body2" color="text.secondary">
                    <Paper elevation={0} sx={notePaperSx}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        {noteIcon}
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: isCustomerNote ? 'secondary.main' : undefined,
                          }}
                        >
                          {noteHeading}
                        </Typography>
                      </Box>
                      {entry.note.trim().length > 0 ? (
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {entry.note}
                        </Typography>
                      ) : null}
                      {entry.attachments && entry.attachments.length > 0 ? (
                        <Box sx={{ mt: entry.note.trim().length > 0 ? 1 : 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {entry.attachments.map((att) => (
                            <Link
                              key={att.storage_path}
                              href={att.download_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              variant="body2"
                              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontWeight: 500 }}
                            >
                              <AttachFileIcon fontSize="inherit" sx={{ flexShrink: 0 }} />
                              {att.file_name}
                            </Link>
                          ))}
                        </Box>
                      ) : null}
                      {!entry.note.trim() && (!entry.attachments || entry.attachments.length === 0) ? (
                        <Typography variant="body2" color="text.secondary">
                          (Empty note)
                        </Typography>
                      ) : null}
                      {entry.userId && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                          Added by {userIdToDisplayName[entry.userId] ?? 'Unknown'}
                        </Typography>
                      )}
                    </Paper>
                  </TimelineOppositeContent>
                  <TimelineSeparator>
                    <TimelineDot color={noteDotColor} variant={isCustomerNote ? 'filled' : 'outlined'}>
                      {isCustomerNote ? <PersonIcon /> : <NoteIcon />}
                    </TimelineDot>
                    {entryIndex < logisticsTimelineDisplayEntries.length - 1 && <TimelineConnector />}
                  </TimelineSeparator>
                  <TimelineContent sx={{ py: '12px', px: 2 }}>
                    <Box sx={{ textAlign: 'left' }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                        {formatDate(entry.timestamp)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {getTimeAgo(entry.timestamp)}
                      </Typography>
                    </Box>
                  </TimelineContent>
                </TimelineItem>
              )
            }

            if (entry.type === 'vehicle_paid_for') {
              return (
                <TimelineItem key={entry.key}>
                  <TimelineOppositeContent sx={{ m: 'auto 0' }} align="right" variant="body2" color="text.secondary">
                    <Paper elevation={0} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2, maxWidth: 320 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <PaymentIcon color="success" fontSize="small" />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Paid
                        </Typography>
                      </Box>
                    </Paper>
                  </TimelineOppositeContent>
                  <TimelineSeparator>
                    <TimelineDot color="success" variant="outlined">
                      <PaymentIcon />
                    </TimelineDot>
                    {entryIndex < logisticsTimelineDisplayEntries.length - 1 && <TimelineConnector />}
                  </TimelineSeparator>
                  <TimelineContent sx={{ py: '12px', px: 2 }}>
                    <Box sx={{ textAlign: 'left' }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                        {formatDate(entry.timestamp)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {getTimeAgo(entry.timestamp)}
                      </Typography>
                    </Box>
                  </TimelineContent>
                </TimelineItem>
              )
            }

            if (entry.type === 'vehicle_purchased_for_inventory') {
              return (
                <TimelineItem key={entry.key}>
                  <TimelineOppositeContent sx={{ m: 'auto 0' }} align="right" variant="body2" color="text.secondary">
                    <Paper elevation={0} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2, maxWidth: 320 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <ShoppingCartIcon color="warning" fontSize="small" />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Purchased for inventory
                        </Typography>
                      </Box>
                      {entry.whoName && (
                        <Typography variant="body2" color="text.secondary">
                          Purchased from {entry.whoName}
                        </Typography>
                      )}
                    </Paper>
                  </TimelineOppositeContent>
                  <TimelineSeparator>
                    <TimelineDot color="warning" variant="outlined">
                      <ShoppingCartIcon />
                    </TimelineDot>
                    {entryIndex < logisticsTimelineDisplayEntries.length - 1 && <TimelineConnector />}
                  </TimelineSeparator>
                  <TimelineContent sx={{ py: '12px', px: 2 }}>
                    <Box sx={{ textAlign: 'left' }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                        {formatDate(entry.timestamp)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {getTimeAgo(entry.timestamp)}
                      </Typography>
                    </Box>
                  </TimelineContent>
                </TimelineItem>
              )
            }

            if (entry.type === 'vehicle_funded_date') {
              return (
                <TimelineItem key={entry.key}>
                  <TimelineOppositeContent sx={{ m: 'auto 0' }} align="right" variant="body2" color="text.secondary">
                    <Paper elevation={0} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2, maxWidth: 320 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <AccountBalanceIcon color="info" fontSize="small" />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Funded
                        </Typography>
                      </Box>
                    </Paper>
                  </TimelineOppositeContent>
                  <TimelineSeparator>
                    <TimelineDot color="info" variant="outlined">
                      <AccountBalanceIcon />
                    </TimelineDot>
                    {entryIndex < logisticsTimelineDisplayEntries.length - 1 && <TimelineConnector />}
                  </TimelineSeparator>
                  <TimelineContent sx={{ py: '12px', px: 2 }}>
                    <Box sx={{ textAlign: 'left' }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                        {formatDate(entry.timestamp)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {getTimeAgo(entry.timestamp)}
                      </Typography>
                    </Box>
                  </TimelineContent>
                </TimelineItem>
              )
            }

            if (entry.type === 'vehicle_expense') {
              const attachmentCount = entry.attachmentUrls.length
              return (
                <TimelineItem key={entry.key}>
                  <TimelineOppositeContent sx={{ m: 'auto 0' }} align="right" variant="body2" color="text.secondary">
                    <Paper elevation={0} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2, maxWidth: 320 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <ReceiptIcon color="warning" fontSize="small" />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Expense · {entry.categoryLabel}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {formatExpenseAmount(entry.amount)}
                        {entry.paid ? ' · Paid' : ' · Unpaid'}
                      </Typography>
                      {entry.businessName && (
                        <Typography variant="body2" color="text.secondary">
                          {entry.businessName}
                        </Typography>
                      )}
                      {entry.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                          {entry.description}
                        </Typography>
                      )}
                      {attachmentCount > 0 && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<AttachFileIcon />}
                          endIcon={<OpenInNewIcon />}
                          onClick={() => {
                            entry.attachmentUrls.forEach((url) => {
                              window.open(url, '_blank', 'noopener,noreferrer')
                            })
                          }}
                          sx={{ mt: 1 }}
                        >
                          {attachmentCount === 1
                            ? 'Open attachment'
                            : `Open attachments (${attachmentCount})`}
                        </Button>
                      )}
                    </Paper>
                  </TimelineOppositeContent>
                  <TimelineSeparator>
                    <TimelineDot color="warning" variant="outlined">
                      <ReceiptIcon />
                    </TimelineDot>
                    {entryIndex < logisticsTimelineDisplayEntries.length - 1 && <TimelineConnector />}
                  </TimelineSeparator>
                  <TimelineContent sx={{ py: '12px', px: 2 }}>
                    <Box sx={{ textAlign: 'left' }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                        {formatDate(entry.timestamp)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {getTimeAgo(entry.timestamp)}
                      </Typography>
                    </Box>
                  </TimelineContent>
                </TimelineItem>
              )
            }

            if (entry.type === 'inventory_status_date') {
              const icon =
                entry.statusLabel === 'Active Deal'
                  ? <Assignment color="info" fontSize="small" />
                  : entry.statusLabel === 'Available'
                    ? <CheckCircle color="success" fontSize="small" />
                    : entry.statusLabel === 'Pending Available'
                      ? <Schedule color="warning" fontSize="small" />
                      : entry.statusLabel === 'Sold'
                        ? <SellIcon color="primary" fontSize="small" />
                        : <SellIcon color="warning" fontSize="small" />
              const timelineDotColor =
                entry.statusLabel === 'Active Deal'
                  ? 'info'
                  : entry.statusLabel === 'Available'
                    ? 'success'
                    : entry.statusLabel === 'Sold'
                      ? 'primary'
                      : 'warning'
              return (
                <TimelineItem key={entry.key}>
                  <TimelineOppositeContent sx={{ m: 'auto 0' }} align="right" variant="body2" color="text.secondary">
                    <Paper elevation={0} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2, maxWidth: 320 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        {icon}
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {entry.statusLabel === 'Sold'
                            ? entry.relatedUserName
                              ? `Sold to ${entry.relatedUserName}`
                              : 'Sold'
                            : entry.statusLabel}
                        </Typography>
                      </Box>
                      {entry.relatedUserName && entry.statusLabel !== 'Sold' && (
                        <Typography variant="body2" color="text.secondary">
                          {entry.statusLabel === 'Pending Sale' || entry.statusLabel === 'Active Deal'
                            ? `Customer: ${entry.relatedUserName}`
                            : entry.relatedUserName}
                        </Typography>
                      )}
                    </Paper>
                  </TimelineOppositeContent>
                  <TimelineSeparator>
                    <TimelineDot color={timelineDotColor} variant="outlined">
                      {icon}
                    </TimelineDot>
                    {entryIndex < logisticsTimelineDisplayEntries.length - 1 && <TimelineConnector />}
                  </TimelineSeparator>
                  <TimelineContent sx={{ py: '12px', px: 2 }}>
                    <Box sx={{ textAlign: 'left' }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                        {formatDate(entry.timestamp)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {getTimeAgo(entry.timestamp)}
                      </Typography>
                    </Box>
                  </TimelineContent>
                </TimelineItem>
              )
            }

            if (entry.type === 'manual_transporter_delivered') {
              return (
                <TimelineItem key={entry.key}>
                  <TimelineOppositeContent sx={{ m: 'auto 0' }} align="right" variant="body2" color="text.secondary">
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                        {formatDate(entry.timestamp)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {getTimeAgo(entry.timestamp)}
                      </Typography>
                    </Box>
                  </TimelineOppositeContent>
                  <TimelineSeparator>
                    <TimelineDot color="success" variant="filled">
                      <DirectionsCar />
                    </TimelineDot>
                    {entryIndex < logisticsTimelineDisplayEntries.length - 1 && <TimelineConnector />}
                  </TimelineSeparator>
                  <TimelineContent sx={{ py: '12px', px: 2 }}>
                    <Paper elevation={0} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2, maxWidth: 420 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Chip label="Delivered" size="small" color="success" sx={{ fontWeight: 600 }} />
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Truck delivered to customer
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Manually marked delivered by admin.
                      </Typography>
                      {entry.userId && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                          Added by {userIdToDisplayName[entry.userId] ?? 'Unknown'}
                        </Typography>
                      )}
                    </Paper>
                  </TimelineContent>
                </TimelineItem>
              )
            }

            if (entry.type === 'leg_status') {
              const resolveLegLocationLabel = (locationId: string, locationType: string) => {
                if (locationType === 'repair') {
                  return repairLocationById.get(locationId)?.label ?? locationId
                }
                return pickupLocationById.get(locationId)?.label ?? locationId
              }
              const fromLabel = resolveLegLocationLabel(entry.fromId, entry.fromType)
              const toLabel = resolveLegLocationLabel(entry.toId, entry.toType)
              return (
                <TimelineItem key={entry.key}>
                  <TimelineOppositeContent sx={{ m: 'auto 0' }} align="right" variant="body2" color="text.secondary">
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                        {formatDate(entry.timestamp)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {getTimeAgo(entry.timestamp)}
                      </Typography>
                    </Box>
                  </TimelineOppositeContent>
                  <TimelineSeparator>
                    <TimelineDot color="warning" variant="outlined">
                      <DirectionsCar />
                    </TimelineDot>
                    {entryIndex < logisticsTimelineDisplayEntries.length - 1 && <TimelineConnector />}
                  </TimelineSeparator>
                  <TimelineContent sx={{ py: '12px', px: 2 }}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1.5,
                        border: '2px solid',
                        borderColor: 'warning.main',
                        bgcolor: (t) => alpha(t.palette.warning.main, 0.06),
                        borderRadius: 2,
                        maxWidth: 420,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mb: 0.5 }}>
                        <Chip
                          label={formatStatusLabel(entry.status)}
                          size="small"
                          color="warning"
                          sx={{ fontWeight: 600 }}
                        />
                        <Chip label="Post-delivery" size="small" color="warning" />
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Shipment leg {entry.legIndex + 1}: {fromLabel} → {toLabel}
                      </Typography>
                      {entry.notes ? (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          {entry.notes}
                        </Typography>
                      ) : null}
                    </Paper>
                  </TimelineContent>
                </TimelineItem>
              )
            }

            if (entry.type === 'repair_media_group') {
              const groupTitle =
                entry.photoCount > 0 && entry.videoCount > 0
                  ? 'Media'
                  : entry.photoCount > 0
                    ? 'Photos'
                    : 'Videos'
              return (
                <TimelineItem key={entry.key}>
                  <TimelineOppositeContent sx={{ m: 'auto 0' }} align="right" variant="body2" color="text.secondary">
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                        {formatDate(entry.timestamp)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {getTimeAgo(entry.timestamp)}
                      </Typography>
                    </Box>
                  </TimelineOppositeContent>
                  <TimelineSeparator>
                    <TimelineDot color="primary" variant="outlined">
                      {entry.videoCount > 0 ? <VideocamIcon /> : <PhotoIcon />}
                    </TimelineDot>
                    {entryIndex < logisticsTimelineDisplayEntries.length - 1 && <TimelineConnector />}
                  </TimelineSeparator>
                  <TimelineContent sx={{ py: '12px', px: 2 }}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1.5,
                        border: entry.isPostDeliveryRepair ? '2px solid' : '1px solid',
                        borderColor: entry.isPostDeliveryRepair ? 'warning.main' : 'divider',
                        bgcolor: entry.isPostDeliveryRepair
                          ? (theme) => alpha(theme.palette.warning.main, 0.06)
                          : undefined,
                        borderRadius: 2,
                        maxWidth: 420,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'space-between', mb: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                          <Chip label={`Repair ${groupTitle}`} size="small" color="error" sx={{ fontWeight: 600 }} />
                          {entry.isPostDeliveryRepair && (
                            <Chip label="Post-delivery" size="small" color="warning" />
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Chip size="small" icon={<PhotoIcon sx={{ fontSize: 14 }} />} label={entry.photoCount} variant="outlined" />
                          <Chip size="small" icon={<VideocamIcon sx={{ fontSize: 14 }} />} label={entry.videoCount} variant="outlined" />
                        </Box>
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                        {entry.entries.length} media uploads
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => openRepairMediaGallery(entry.entries)}
                        sx={{ textTransform: 'none' }}
                      >
                        View media
                      </Button>
                    </Paper>
                  </TimelineContent>
                </TimelineItem>
              )
            }

            const isRepairEntry =
              entry.type === 'repair_status' ||
              entry.type === 'repair_note' ||
              entry.type === 'repair_paid' ||
              entry.type === 'pdi_completed'
            const normalizedTruckStatus =
              entry.type === 'truck_status' ? entry.status.toLowerCase().replace(/\s+/g, '_') : null
            const truckPlan = entry.type === 'truck_status' ? shipmentPlanByTruckId.get(entry.truckId) : undefined
            const firstLeg = truckPlan?.shipment_legs?.[0]
            const finalLeg = truckPlan?.shipment_legs?.[truckPlan.shipment_legs.length - 1]
            const truckRecord =
              entry.type === 'truck_status'
                ? (shipmentRepairData?.trucks ?? []).find((truck) => truck.id === entry.truckId)
                : undefined
            const currentRepairTruck =
              entry.type === 'truck_status'
                ? repairTruckByTransportTruckId.get(entry.truckId)
                : entry.type === 'repair_status' ||
                    entry.type === 'repair_note' ||
                    entry.type === 'repair_paid' ||
                    entry.type === 'pdi_completed'
                  ? (shipmentRepairData?.repairTrucks ?? []).find(
                      (repairTruck) => repairTruck.id === entry.repairTruckId
                    )
                  : undefined
            const getLocationLabel = (locationId: string, locationType: string) => {
              if (locationType === 'repair') {
                return repairLocationById.get(locationId)?.label ?? locationId
              }
              return pickupLocationById.get(locationId)?.label ?? locationId
            }
            const getLocationContact = (locationId: string, locationType: string) => {
              if (locationType === 'repair') {
                return repairLocationById.get(locationId)?.contactName
              }
              return pickupLocationById.get(locationId)?.contactName
            }
            const getLocationNameById = (locationId?: string) => {
              if (!locationId) return null
              return repairLocationById.get(locationId)?.label ?? pickupLocationById.get(locationId)?.label ?? null
            }
            const isAwaitingTransportStatus =
              normalizedTruckStatus === 'assigned' ||
              normalizedTruckStatus === 'awaiting_transport'
            const inTransitLeg =
              entry.type === 'truck_status' && normalizedTruckStatus === 'in_transit'
                ? findClosestLegForTimestamp(truckPlan, entry.timestamp)
                : undefined
            const postDeliveryLeg =
              entry.type === 'truck_status' &&
              (normalizedTruckStatus === 'delivered' || normalizedTruckStatus === 'repair')
                ? findClosestLegForTimestamp(truckPlan, entry.timestamp)
                : undefined
            const awaitingTransportLeg =
              entry.type === 'truck_status' && isAwaitingTransportStatus
                ? findClosestLegForTimestamp(truckPlan, entry.timestamp)
                : undefined
            const awaitingTransportNextLocationLabel =
              awaitingTransportLeg?.to_id != null && String(awaitingTransportLeg.to_id).trim() !== ''
                ? getLocationLabel(awaitingTransportLeg.to_id, awaitingTransportLeg.to_type)
                : null
            const inTransitLegIndex =
              inTransitLeg && truckPlan ? getShipmentLegIndex(truckPlan, inTransitLeg) : -1
            const postDeliveryLegIndex =
              postDeliveryLeg && truckPlan ? getShipmentLegIndex(truckPlan, postDeliveryLeg) : -1
            const assignedCompanyId = getCompanyIdForLegAtTimestamp(
              awaitingTransportLeg,
              entry.type === 'truck_status' ? entry.timestamp : undefined
            )
            const assignedCompanyName = assignedCompanyId
              ? transporterGroupOwnersData?.[assignedCompanyId]?.group_name ||
                [
                  transporterGroupOwnersData?.[assignedCompanyId]?.first_name,
                  transporterGroupOwnersData?.[assignedCompanyId]?.last_name,
                ]
                  .filter(Boolean)
                  .join(' ')
                  .trim() ||
                transporterGroupOwnersData?.[assignedCompanyId]?.email ||
                assignedCompanyId
              : undefined
            const getDriverName = (driverId?: string) => {
              if (!driverId) return null
              const driver = transportersMap[driverId]
              if (!driver) return driverId
              const fullName = [driver.first_name, driver.last_name].filter(Boolean).join(' ').trim()
              return fullName || driver.email || driverId
            }
            const firstLegDriverName = getDriverName(firstLeg?.driver_id)
            const inTransitDriverName = getDriverName(inTransitLeg?.driver_id)
            const assignedCurrentLocationName = getLocationNameById(truckRecord?.currentLocationId)
            const repairShopName =
              entry.type === 'truck_status' && normalizedTruckStatus === 'repair'
                ? (() => {
                    const atMs = timelineTimestampToMs(entry.timestamp)
                    const candidates = (shipmentRepairData?.repairTrucks ?? []).filter(
                      (repairTruck) => repairTruck.transport_truck_id === entry.truckId
                    )
                    if (candidates.length === 0) return undefined
                    let best = candidates[0]
                    let bestDist = Math.abs(timelineTimestampToMs(best.created_at) - atMs)
                    for (let i = 1; i < candidates.length; i++) {
                      const candidate = candidates[i]
                      const dist = Math.abs(timelineTimestampToMs(candidate.created_at) - atMs)
                      if (dist < bestDist) {
                        best = candidate
                        bestDist = dist
                      }
                    }
                    return (
                      repairLocationById.get(best.repair_shop_location_id)?.label ??
                      best.repair_shop_location_id
                    )
                  })()
                : undefined
            const finalDeliveryContact =
              entry.type === 'truck_status' && normalizedTruckStatus === 'delivered' && finalLeg
                ? getLocationContact(finalLeg.to_id, finalLeg.to_type) ??
                  getLocationLabel(finalLeg.to_id, finalLeg.to_type)
                : undefined
            const truckStatusDotColor: 'grey' | 'info' | 'primary' | 'error' | 'success' =
              normalizedTruckStatus === 'draft'
                ? 'grey'
                : isAwaitingTransportStatus
                  ? 'info'
                  : normalizedTruckStatus === 'in_transit'
                    ? 'primary'
                    : normalizedTruckStatus === 'repair'
                      ? 'error'
                      : normalizedTruckStatus === 'delivered'
                        ? 'success'
                        : 'primary'
            const truckStatusChipColor: 'default' | 'info' | 'primary' | 'error' | 'success' =
              normalizedTruckStatus === 'draft'
                ? 'default'
                : isAwaitingTransportStatus
                  ? 'info'
                  : normalizedTruckStatus === 'in_transit'
                    ? 'primary'
                    : normalizedTruckStatus === 'repair'
                      ? 'error'
                      : normalizedTruckStatus === 'delivered'
                        ? 'success'
                        : 'primary'
            const truckStatusNote =
              normalizedTruckStatus === 'draft'
                ? 'Truck added to the transport system'
                : isAwaitingTransportStatus
                  ? 'Truck awaiting transport pickup'
                  : normalizedTruckStatus === 'in_transit'
                    ? 'Truck is in transit'
                  : normalizedTruckStatus === 'repair'
                    ? 'Truck dropped off at repair shop'
                    : normalizedTruckStatus === 'delivered'
                      ? 'Truck delivered to customer'
                      : null
            const truckStatusSubtext =
              isAwaitingTransportStatus && assignedCompanyName
                ? `${assignedCompanyName}`
                : normalizedTruckStatus === 'in_transit' && inTransitLeg
                  ? `${getLocationLabel(inTransitLeg.from_id, inTransitLeg.from_type)} -> ${getLocationLabel(inTransitLeg.to_id, inTransitLeg.to_type)}`
                  : normalizedTruckStatus === 'repair' && repairShopName
                    ? `${repairShopName}`
                    : normalizedTruckStatus === 'delivered' && finalDeliveryContact
                      ? `${finalDeliveryContact}`
                      : null
            const expandedLegDetails = (truckPlan?.shipment_legs ?? []).map((leg, legIndex) => {
              const legStatusHistory = leg.status_history ?? []
              const latestLegStatus = legStatusHistory[legStatusHistory.length - 1]?.status ?? 'Draft'
              const legCompanyName = leg.company_id
                ? transporterGroupOwnersData?.[leg.company_id]?.group_name ||
                  [
                    transporterGroupOwnersData?.[leg.company_id]?.first_name,
                    transporterGroupOwnersData?.[leg.company_id]?.last_name,
                  ]
                    .filter(Boolean)
                    .join(' ')
                    .trim() ||
                  transporterGroupOwnersData?.[leg.company_id]?.email ||
                  leg.company_id
                : 'Unassigned'
              return {
                key: `leg-detail-${legIndex}-${leg.from_id}-${leg.to_id}`,
                label: `${getLocationLabel(leg.from_id, leg.from_type)} -> ${getLocationLabel(leg.to_id, leg.to_type)}`,
                latestLegStatus,
                company: legCompanyName,
                driver: getDriverName(leg.driver_id) ?? 'Unassigned',
                assignmentNotes: leg.assignment_notes,
                ready: leg.ready,
                readyDate: leg.ready_date,
                estimatedShippingPrice: leg.estimated_shipping_price,
                gatePassCount: leg.gate_passes?.length ?? 0,
                statusHistory: legStatusHistory,
              }
            })
            const isRepairWorkComplete =
              entry.type === 'repair_status' && (entry.status ?? '').toLowerCase() === 'complete'
            const statusLabel =
              entry.type === 'truck_status'
                ? formatStatusLabel(entry.status)
                : entry.type === 'repair_status'
                  ? `Repair: ${formatStatusLabel(entry.status)}`
                  : entry.type === 'repair_paid'
                    ? entry.paid
                      ? 'Repair paid'
                      : 'Repair payment pending'
                    : entry.type === 'pdi_completed'
                      ? 'PDI completed'
                      : entry.type === 'repair_note' && entry.mediaType === 'photo'
                        ? 'Repair photo'
                        : entry.type === 'repair_note' && entry.mediaType === 'damage_photo'
                          ? 'Damage photo'
                        : entry.type === 'repair_note' && entry.mediaType === 'video'
                          ? 'Repair video'
                          : entry.type === 'repair_note' && entry.mediaType === 'invoice'
                            ? 'Repair invoice'
                            : entry.type === 'repair_note' && entry.mediaType === 'estimate'
                              ? 'Repair estimate'
                            : 'Repair note'
            const dotColor: 'grey' | 'info' | 'primary' | 'error' | 'warning' | 'success' =
              entry.type === 'truck_status'
                ? truckStatusDotColor
                : entry.type === 'repair_status'
                  ? isRepairWorkComplete
                    ? 'success'
                    : 'error'
                  : entry.type === 'repair_paid'
                    ? entry.paid
                      ? 'primary'
                      : 'warning'
                    : entry.type === 'pdi_completed'
                      ? 'success'
                      : 'error'
            const chipColor: 'default' | 'info' | 'primary' | 'error' | 'warning' | 'success' =
              entry.type === 'truck_status'
                ? truckStatusChipColor
                : dotColor === 'grey'
                  ? 'default'
                  : dotColor
            const icon =
              entry.type === 'truck_status'
                ? <DirectionsCar />
                : entry.type === 'repair_paid'
                  ? <PaymentIcon />
                  : entry.type === 'pdi_completed' || isRepairWorkComplete
                    ? <CheckCircle />
                    : <Build />
            const isTruckStatusEntry = entry.type === 'truck_status'
            const isExpandableTruckStatus =
              isTruckStatusEntry &&
              (isAwaitingTransportStatus ||
                normalizedTruckStatus === 'in_transit' ||
                normalizedTruckStatus === 'repair')
            const isExpanded = Boolean(expandedLogisticsEntries[entry.key])

            const isPostDeliveryActivity =
              'isPostDeliveryRepair' in entry && Boolean(entry.isPostDeliveryRepair)

            return (
              <TimelineItem key={entry.key}>
                <TimelineOppositeContent sx={{ m: 'auto 0' }} align="right" variant="body2" color="text.secondary">
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      {formatDate(entry.timestamp)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {getTimeAgo(entry.timestamp)}
                    </Typography>
                  </Box>
                </TimelineOppositeContent>
                <TimelineSeparator>
                  <TimelineDot color={dotColor} variant={isRepairEntry || isVehicleSideEntry ? 'outlined' : 'filled'}>
                    {icon}
                  </TimelineDot>
                  {entryIndex < logisticsTimelineDisplayEntries.length - 1 && <TimelineConnector />}
                </TimelineSeparator>
                <TimelineContent sx={{ py: '12px', px: 2 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.5,
                      border: isPostDeliveryActivity ? '2px solid' : '1px solid',
                      borderColor: isPostDeliveryActivity ? 'warning.main' : 'divider',
                      bgcolor: isPostDeliveryActivity
                        ? (theme) => alpha(theme.palette.warning.main, 0.06)
                        : undefined,
                      borderRadius: 2,
                      maxWidth: 420,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                        <Chip
                          label={statusLabel}
                          size="small"
                          color={chipColor}
                          sx={{
                            fontWeight: 600,
                            ...(entry.type === 'truck_status' && normalizedTruckStatus === 'draft'
                              ? { bgcolor: 'grey.200', color: 'text.primary' }
                              : {}),
                          }}
                        />
                        {isPostDeliveryActivity && (
                          <Chip label="Post-delivery" size="small" color="warning" />
                        )}
                      </Box>
                      {isExpandableTruckStatus && (
                        <IconButton
                          size="small"
                          onClick={() => toggleLogisticsEntryExpanded(entry.key)}
                          title={isExpanded ? 'Hide details' : 'Show details'}
                        >
                          {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        </IconButton>
                      )}
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {entry.type === 'truck_status'
                        ? (truckStatusNote ?? `Logistics status updated to ${formatStatusLabel(entry.status)}`)
                        : entry.type === 'repair_status'
                          ? `Repair status updated to ${formatStatusLabel(entry.status)}`
                          : entry.type === 'pdi_completed'
                            ? 'Pre-delivery inspection checklist completed'
                            : entry.type === 'repair_note'
                              ? entry.note
                              : entry.type === 'repair_paid' && entry.paid
                                ? 'Repair payment completed'
                                : 'Repair payment marked pending'}
                    </Typography>
                    {entry.type === 'pdi_completed' && currentRepairTruck?.repair_shop_location_id && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        Repair shop:{' '}
                        {repairLocationById.get(currentRepairTruck.repair_shop_location_id)?.label ??
                          currentRepairTruck.repair_shop_location_id}
                      </Typography>
                    )}
                    {entry.type === 'pdi_completed' && currentRepairTruck && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setInspectionChecklistViewRepairTruck(currentRepairTruck)}
                        sx={{ textTransform: 'none', mt: 1, alignSelf: 'flex-start' }}
                        startIcon={<Assignment fontSize="small" />}
                      >
                        View checklist answers
                      </Button>
                    )}
                    {entry.type === 'repair_note' &&
                      entry.mediaStoragePath &&
                      (entry.mediaType === 'photo' ||
                        entry.mediaType === 'video' ||
                        entry.mediaType === 'damage_photo') && (
                        <Box sx={{ mt: 1 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => openRepairMediaGallery([entry])}
                            sx={{ textTransform: 'none' }}
                          >
                            View {entry.mediaType === 'damage_photo' ? 'damage photo' : entry.mediaType}
                          </Button>
                        </Box>
                      )}
                    {entry.type === 'repair_note' &&
                      entry.mediaStoragePath &&
                      (entry.mediaType === 'invoice' || entry.mediaType === 'estimate') && (
                        <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                          <RepairPdfLink
                            storagePath={entry.mediaStoragePath}
                            label={entry.mediaType === 'estimate' ? 'estimate' : 'invoice'}
                          />
                          {entry.mediaType === 'invoice' && entry.repairTruckId ? (
                            <RepairInvoiceExpenseActions
                              storagePath={entry.mediaStoragePath}
                              repairTruckId={entry.repairTruckId}
                              repairShopId={
                                repairTruckById.get(entry.repairTruckId)?.repair_shop_location_id ?? ''
                              }
                              vehicleId={vehicle?.id ?? null}
                              note={entry.note?.trim() || undefined}
                            />
                          ) : null}
                        </Box>
                      )}
                    {entry.type === 'truck_status' && truckStatusSubtext && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {truckStatusSubtext}
                      </Typography>
                    )}
                    {entry.type === 'truck_status' &&
                      normalizedTruckStatus === 'in_transit' &&
                      truckPlan?.id &&
                      inTransitLegIndex >= 0 &&
                      legPhotosStatusByPlanId.get(truckPlan.id)?.[inTransitLegIndex]?.hasPreDelivery && (
                        <Box sx={{ mt: 1 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<PhotoIcon fontSize="small" />}
                            onClick={() =>
                              void handleViewLegPhotos(
                                entry.key,
                                truckPlan.id,
                                inTransitLegIndex,
                                'preDelivery'
                              )
                            }
                            disabled={loadingLegPhotosKey === entry.key}
                            sx={{ textTransform: 'none' }}
                          >
                            {loadingLegPhotosKey === entry.key
                              ? 'Loading...'
                              : 'View pre-delivery photos'}
                          </Button>
                        </Box>
                      )}
                    {entry.type === 'truck_status' &&
                      (normalizedTruckStatus === 'delivered' || normalizedTruckStatus === 'repair') &&
                      truckPlan?.id &&
                      postDeliveryLegIndex >= 0 &&
                      legPhotosStatusByPlanId.get(truckPlan.id)?.[postDeliveryLegIndex]?.hasPostDelivery && (
                        <Box sx={{ mt: 1 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<PhotoIcon fontSize="small" />}
                            onClick={() =>
                              void handleViewLegPhotos(
                                entry.key,
                                truckPlan.id,
                                postDeliveryLegIndex,
                                'postDelivery'
                              )
                            }
                            disabled={loadingLegPhotosKey === entry.key}
                            sx={{ textTransform: 'none' }}
                          >
                            {loadingLegPhotosKey === entry.key
                              ? 'Loading...'
                              : 'View post-delivery photos'}
                          </Button>
                        </Box>
                      )}
                    <Collapse in={isExpandableTruckStatus && isExpanded} timeout="auto" unmountOnExit>
                      <Box sx={{ mt: 1, pt: 1, borderTop: '1px dashed', borderColor: 'divider' }}>
                        {entry.type === 'truck_status' && (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {normalizedTruckStatus === 'repair' ? (
                              <>
                                <Typography variant="caption" color="text.secondary">
                                  Repair shop: {repairLocationById.get(currentRepairTruck?.repair_shop_location_id ?? '')?.label ?? currentRepairTruck?.repair_shop_location_id ?? 'Unknown'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Paid: {currentRepairTruck?.paid ? 'Yes' : 'No'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Flagged: {currentRepairTruck?.flagged ? 'Yes' : 'No'} | Being painted: {currentRepairTruck?.being_painted ? 'Yes' : 'No'}
                                </Typography>
                              </>
                            ) : isAwaitingTransportStatus ? (
                              <>
                                <Typography variant="caption" color="text.secondary">
                                  Current location: {assignedCurrentLocationName ?? 'Unknown location'}
                                </Typography>
                                {awaitingTransportNextLocationLabel ? (
                                  <Typography variant="caption" color="text.secondary">
                                    Next location: {awaitingTransportNextLocationLabel}
                                  </Typography>
                                ) : null}
                              </>
                            ) : normalizedTruckStatus === 'in_transit' ? (
                              <>
                                <Typography variant="caption" color="text.secondary">
                                  Driver: {inTransitDriverName ?? 'Unassigned'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Assignment notes: {inTransitLeg?.assignment_notes?.trim() || 'None'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Dispatch information: {inTransitLeg?.dispatch_info?.trim() || 'None'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Estimated price:{' '}
                                  {typeof inTransitLeg?.estimated_shipping_price === 'number'
                                    ? `$${inTransitLeg.estimated_shipping_price.toLocaleString('en-US', {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      })}`
                                    : 'Not set'}
                                </Typography>
                              </>
                            ) : (
                              <>
                                <Typography variant="caption" color="text.secondary">
                                  Truck ID: {entry.truckId}
                                </Typography>
                                {truckRecord?.created_at && (
                                  <Typography variant="caption" color="text.secondary">
                                    Added: {formatDate(truckRecord.created_at)}
                                  </Typography>
                                )}
                                {truckPlan?.id && (
                                  <Typography variant="caption" color="text.secondary">
                                    Shipment plan: {truckPlan.id}
                                  </Typography>
                                )}
                                {firstLegDriverName && (
                                  <Typography variant="caption" color="text.secondary">
                                    Assigned driver: {firstLegDriverName}
                                  </Typography>
                                )}
                                {truckRecord?.currentLocationId && (
                                  <Typography variant="caption" color="text.secondary">
                                    Current location ID: {truckRecord.currentLocationId}
                                  </Typography>
                                )}
                                {expandedLegDetails.length > 0 && (
                                  <Box sx={{ mt: 0.5 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                                      Shipment leg details
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
                                      {expandedLegDetails.map((leg) => (
                                        <Paper
                                          key={leg.key}
                                          variant="outlined"
                                          sx={{ p: 0.75, borderRadius: 1.5, bgcolor: 'background.default' }}
                                        >
                                          <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>
                                            {leg.label}
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                            Status: {leg.latestLegStatus} | Company: {leg.company}
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                            Driver: {leg.driver} | Ready: {leg.ready ? 'Yes' : 'No'}
                                          </Typography>
                                          {leg.readyDate && (
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                              Ready date: {leg.readyDate}
                                            </Typography>
                                          )}
                                          {typeof leg.estimatedShippingPrice === 'number' && (
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                              Est. shipping: ${leg.estimatedShippingPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </Typography>
                                          )}
                                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                            Gate passes: {leg.gatePassCount}
                                          </Typography>
                                          {leg.assignmentNotes && (
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                              Notes: {leg.assignmentNotes}
                                            </Typography>
                                          )}
                                          {leg.statusHistory.length > 0 && (
                                            <Box sx={{ mt: 0.4 }}>
                                              {leg.statusHistory.map((history, idx) => (
                                                <Typography key={`${leg.key}-history-${idx}`} variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                  - {history.status} at {formatDate(history.updated_at)}
                                                </Typography>
                                              ))}
                                            </Box>
                                          )}
                                        </Paper>
                                      ))}
                                    </Box>
                                  </Box>
                                )}
                              </>
                            )}
                          </Box>
                        )}
                      </Box>
                    </Collapse>
                    {'userId' in entry && entry.userId && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                        By {userIdToDisplayName[entry.userId] ?? 'Unknown'}
                      </Typography>
                    )}
                  </Paper>
                </TimelineContent>
              </TimelineItem>
            )
          })}
        </Timeline>
      )}
    </>
  )

  const titleTimelineSteps = useMemo<TitleTimelineStep[]>(() => {
    const titleTracking = vehicle?.title_tracking
    if (!vehicle) return []

    // Match Title Manager flow:
    // Inbound -> Titles In House -> Outbound.
    // The in-house flow has a branch:
    // - title_flip_required (with in progress/completed)
    // - or title_flip_not_required (skips in progress/completed)
    const effectiveSequence = TITLE_TIMELINE_SEQUENCE.filter((step) => {
      if (step.status === 'title_flip_required') {
        return titleTracking?.title_flip_required !== false
      }
      if (step.status === 'title_flip_not_required') {
        return titleTracking?.title_flip_required === false
      }
      if (
        (step.status === 'title_flip_in_progress' || step.status === 'title_flip_completed') &&
        titleTracking?.title_flip_required === false
      ) {
        return false
      }
      return true
    })

    const currentStatus = titleTracking?.title_status
    const currentStatusIndex = currentStatus
      ? effectiveSequence.findIndex((step) => step.status === currentStatus)
      : -1

    const mappedSteps = effectiveSequence.map((step, index) => {
      const rawTimestamp = step.getTimestamp(vehicle, titleTracking)
      const parsedDate = rawTimestamp ? new Date(rawTimestamp) : null
      const outboundTrackingNumber =
        step.status === 'title_shipped'
          ? (titleTracking?.outbound_tracking_number || titleTracking?.tracking_number || null)
          : null
      return {
        status: step.status,
        label: step.label,
        timestamp: parsedDate && !isNaN(parsedDate.getTime()) ? parsedDate : null,
        outboundTrackingNumber,
        index,
      }
    })

    // Show only statuses the title has been through:
    // - all steps up to current status in workflow order, and/or
    // - any step that has an explicit timestamp recorded.
    return mappedSteps
      .filter((step) => {
        const reachedByCurrentStatus = currentStatusIndex >= 0 && step.index <= currentStatusIndex
        const reachedByTimestamp = Boolean(step.timestamp)
        return reachedByCurrentStatus || reachedByTimestamp
      })
      .map(({ index: _index, ...step }) => step)
  }, [vehicle])

  const currentTitleStatus = vehicle?.title_tracking?.title_status
  const truckNotes = vehicle?.truck_notes?.trim() ?? ''
  const titleTimelineStepsForAll = useMemo(
    () =>
      titleTimelineSteps
        .filter((step): step is TitleTimelineStep & { timestamp: Date } => Boolean(step.timestamp))
        .map((step) => ({
          key: step.status,
          label: step.label,
          timestamp: step.timestamp,
        })),
    [titleTimelineSteps],
  )

  const formatTimeAgoFromMs = (sortMs: number) => {
    const date = new Date(sortMs)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    const diffMinutes = Math.floor(diffMs / (1000 * 60))

    if (diffDays > 0) {
      return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`
    }
    if (diffHours > 0) {
      return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`
    }
    if (diffMinutes > 0) {
      return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`
    }
    return 'Just now'
  }

  const tabOptions = [
    { value: TIMELINE_TAB.all, label: 'All' },
    { value: TIMELINE_TAB.logistics, label: 'Logistics timeline' },
    { value: TIMELINE_TAB.title, label: 'Title timeline' },
    { value: TIMELINE_TAB.customer, label: 'Customer' },
  ]
  const getTitleStatusIcon = (status: TitleTimelineStatus) => {
    switch (status) {
      case 'purchased_for_inventory':
      case 'payment_sent':
        return <PaymentIcon />
      case 'bank_information':
        return <AccountBalanceIcon />
      case 'title_requested':
      case 'shipping_information':
        return <Assignment />
      case 'in_transit':
      case 'title_shipped':
        return <DirectionsCar />
      case 'physical_title_received':
      case 'title_flip_completed':
      case 'completed':
        return <CheckCircle />
      case 'title_flip_required':
      case 'title_flip_not_required':
      case 'title_flip_in_progress':
        return <Build />
      default:
        return <Schedule />
    }
  }

  return (
    <AdminDialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth="lg"
      PaperProps={{
        sx: {
          minHeight: isMobile ? '100dvh' : '700px',
          ...(isMobile ? { height: '100dvh', maxHeight: '100dvh' } : {}),
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6" component="div">
              Vehicle Timeline
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {vehicle.year} {vehicle.make} {vehicle.model} • VIN: {vehicle.vin}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2, ...(isMobile ? { flex: 1, minHeight: 0, overflow: 'auto' } : {}) }}>
        <Paper variant="outlined" sx={{ mb: 2, p: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            Notes
          </Typography>
          {truckNotes ? (
            <Typography
              variant="body2"
              sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
            >
              {truckNotes}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No truck notes.
            </Typography>
          )}
        </Paper>

        <Paper variant="outlined" sx={{ mb: 2, p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Transport SLA timeline
              </Typography>
            </Box>
          </Box>

          {!transportSla.hasAnchorEvent ? (
            <Box sx={{ py: 1 }}>
              <Typography variant="body2" color="text.secondary">
                The timeline starts after a truck is marked paid for.
              </Typography>
            </Box>
          ) : isMobile ? (
            <Stack spacing={1.25}>
              {transportSla.steps.map((step) => {
                const progressColor =
                  step.state === 'completed_on_time'
                    ? 'success.main'
                    : step.state === 'completed_late' || step.state === 'overdue'
                      ? 'error.main'
                      : step.state === 'at_risk'
                        ? 'warning.main'
                        : step.state === 'in_progress'
                          ? 'info.main'
                          : 'action.disabled'

                return (
                  <Paper key={step.key} variant="outlined" sx={{ p: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                      <Typography variant="body2" fontWeight={700}>
                        {step.label}
                      </Typography>
                      <StatusChip
                        dense
                        tone={slaStepStateTone(step.state)}
                        label={getStepStateLabel(step.state)}
                      />
                    </Box>
                    <Box sx={{ height: 6, borderRadius: 999, bgcolor: 'action.hover', overflow: 'hidden', mb: 0.75 }}>
                      <Box
                        sx={{
                          width: `${Math.round(step.progress * 100)}%`,
                          height: '100%',
                          bgcolor: progressColor,
                          transition: 'width 300ms ease',
                        }}
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {formatSlaDuration(step.elapsedMs)} / {formatSlaDuration(step.budgetMs)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Due: {formatSlaDate(step.dueAt)}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        mt: 0.35,
                        fontWeight: 700,
                        color:
                          step.state === 'completed_late' || step.state === 'overdue'
                            ? 'error.main'
                            : step.state === 'completed_on_time'
                              ? 'success.main'
                              : step.state === 'at_risk'
                                ? 'warning.main'
                                : 'text.secondary',
                      }}
                    >
                      {step.helperText}
                    </Typography>
                  </Paper>
                )
              })}
            </Stack>
          ) : (
            <Box sx={{ overflowX: 'visible', pb: 0 }}>
              <Box>
              <Box
                sx={{
                  position: 'relative',
                  display: 'grid',
                  gridTemplateColumns: `repeat(${transportSla.steps.length}, minmax(0, 1fr))`,
                  columnGap: 1.2,
                  mb: 1.2,
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    left: 20,
                    right: 20,
                    top: 14,
                    height: 2,
                    bgcolor: 'divider',
                    zIndex: 0,
                  }}
                />
                {transportSla.steps.map((step) => {
                  const chipColor: 'success' | 'error' | 'warning' | 'info' | 'default' =
                    step.state === 'completed_on_time'
                      ? 'success'
                      : step.state === 'completed_late' || step.state === 'overdue'
                        ? 'error'
                        : step.state === 'at_risk'
                          ? 'warning'
                          : step.state === 'in_progress'
                            ? 'info'
                            : 'default'
                  const dotColor =
                    step.state === 'completed_on_time'
                      ? 'success.main'
                      : step.state === 'completed_late' || step.state === 'overdue'
                        ? 'error.main'
                        : step.state === 'at_risk'
                          ? 'warning.main'
                          : step.state === 'in_progress'
                            ? 'info.main'
                            : 'grey.400'

                  return (
                    <Box key={step.key} sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          bgcolor: 'background.paper',
                          border: '2px solid',
                          borderColor: dotColor,
                          mx: 'auto',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: dotColor }} />
                      </Box>
                      <Typography variant="caption" sx={{ display: 'block', mt: 0.55, fontWeight: 700, px: 0.5, lineHeight: 1.25 }}>
                        {step.label}
                      </Typography>
                      <Chip
                        size="small"
                        color={chipColor}
                        label={getStepStateLabel(step.state)}
                        sx={{ mt: 0.6, fontWeight: 600, maxWidth: '100%' }}
                      />
                    </Box>
                  )
                })}
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(${transportSla.steps.length}, minmax(0, 1fr))`,
                  gap: 1.1,
                }}
              >
                {transportSla.steps.map((step) => {
                  const progressColor =
                    step.state === 'completed_on_time'
                      ? 'success.main'
                      : step.state === 'completed_late' || step.state === 'overdue'
                        ? 'error.main'
                        : step.state === 'at_risk'
                          ? 'warning.main'
                          : step.state === 'in_progress'
                            ? 'info.main'
                            : 'action.disabled'

                  return (
                    <Box key={`${step.key}-detail`} sx={{ minWidth: 0 }}>
                      <Box sx={{ height: 6, borderRadius: 999, bgcolor: 'action.hover', overflow: 'hidden', mb: 0.35 }}>
                        <Box
                          sx={{
                            width: `${Math.round(step.progress * 100)}%`,
                            height: '100%',
                            bgcolor: progressColor,
                            transition: 'width 300ms ease',
                          }}
                        />
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {formatSlaDuration(step.elapsedMs)} / {formatSlaDuration(step.budgetMs)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Due: {formatSlaDate(step.dueAt)}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          fontWeight: 700,
                          color:
                            step.state === 'completed_late' || step.state === 'overdue'
                              ? 'error.main'
                              : step.state === 'completed_on_time'
                                ? 'success.main'
                                : step.state === 'at_risk'
                                  ? 'warning.main'
                                  : 'text.secondary',
                        }}
                      >
                        {step.helperText}
                      </Typography>
                    </Box>
                  )
                })}
              </Box>
              </Box>
            </Box>
          )}
        </Paper>

        {isMobile ? (
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <Select
              value={activeTab}
              onChange={(event) => setActiveTab(Number(event.target.value))}
              displayEmpty
            >
              {tabOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value)} sx={{ mb: 2 }}>
            <Tab value={TIMELINE_TAB.all} label="All" />
            <Tab value={TIMELINE_TAB.logistics} label="Logistics Timeline" />
            <Tab value={TIMELINE_TAB.title} label="Title timeline" />
            <Tab value={TIMELINE_TAB.customer} label="Customer" />
          </Tabs>
        )}
        {activeTab === TIMELINE_TAB.all && vehicle ? (
          <TimelineAllTab
            vehicle={vehicle}
            logisticsEntries={logisticsTimelineDisplayEntries}
            titleSteps={titleTimelineStepsForAll}
            formatTimeAgo={formatTimeAgoFromMs}
          />
        ) : activeTab === TIMELINE_TAB.logistics ? (
          shipmentRepairTimelineContent
        ) : activeTab === TIMELINE_TAB.customer && vehicle ? (
          <TimelineCustomerTab vehicle={vehicle} />
        ) : (
          <Timeline position="right">
            {titleTimelineSteps.map((step, index) => {
              const isCurrentStatus = currentTitleStatus === step.status
              const isExplicitlyCompleted = Boolean(step.timestamp)
              const hasLaterRecordedStep = titleTimelineSteps
                .slice(index + 1)
                .some((later) => Boolean(later.timestamp))
              const isImpliedComplete =
                !isExplicitlyCompleted && hasLaterRecordedStep && !isCurrentStatus
              const showAsComplete = isExplicitlyCompleted || isImpliedComplete
              const dotColor: 'success' | 'primary' | 'grey' =
                showAsComplete ? 'success' : isCurrentStatus ? 'primary' : 'grey'
              return (
                <TimelineItem key={`title-${step.status}`}>
                  <TimelineOppositeContent sx={{ m: 'auto 0' }} align="right" variant="body2" color="text.secondary">
                    {step.timestamp ? (
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {step.timestamp.toLocaleString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </Typography>
                      </Box>
                    ) : isImpliedComplete ? (
                      <Typography variant="body2" color="text.secondary">
                        No date recorded
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Not completed
                      </Typography>
                    )}
                  </TimelineOppositeContent>
                  <TimelineSeparator>
                    <TimelineDot
                      color={dotColor}
                      variant={showAsComplete || isCurrentStatus ? 'filled' : 'outlined'}
                    >
                      {getTitleStatusIcon(step.status)}
                    </TimelineDot>
                    {index < titleTimelineSteps.length - 1 && <TimelineConnector />}
                  </TimelineSeparator>
                  <TimelineContent sx={{ py: '12px', px: 2 }}>
                    <Paper
                      elevation={1}
                      sx={{
                        p: 2,
                        border: '1px solid',
                        borderColor: isCurrentStatus ? 'primary.main' : showAsComplete ? 'success.main' : 'divider',
                        borderRadius: 2,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Chip
                          label={step.label}
                          color={showAsComplete ? 'success' : isCurrentStatus ? 'primary' : 'default'}
                          size="small"
                          sx={{ fontWeight: 600 }}
                        />
                        {isCurrentStatus && (
                          <Chip label="Current status" color="primary" size="small" variant="outlined" />
                        )}
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {isExplicitlyCompleted
                          ? 'Status timestamp recorded'
                          : isImpliedComplete
                            ? 'No timestamp for this step; shown complete because a later milestone was recorded.'
                            : 'No timestamp recorded yet'}
                      </Typography>
                      {step.status === 'title_shipped' && step.outboundTrackingNumber && (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          Outbound tracking:{' '}
                          <Link
                            href={getTrackingUrl(step.outboundTrackingNumber)}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
                          >
                            {step.outboundTrackingNumber}
                          </Link>
                        </Typography>
                      )}
                    </Paper>
                  </TimelineContent>
                </TimelineItem>
              )
            })}
          </Timeline>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        {(activeTab === TIMELINE_TAB.all || activeTab === TIMELINE_TAB.logistics) && userInfo?.isOwner ? (
          <Button
            variant="outlined"
            color={manualDeliveredNoteOriginalIndex >= 0 ? 'warning' : 'success'}
            disabled={!vehicle?.id || addNote.isPending || deleteNote.isPending}
            onClick={() =>
              manualDeliveredNoteOriginalIndex >= 0
                ? setUnmarkDeliveredConfirmOpen(true)
                : setMarkDeliveredConfirmOpen(true)
            }
          >
            {manualDeliveredNoteOriginalIndex >= 0 ? 'Unmark delivered' : 'Mark delivered'}
          </Button>
        ) : null}
        {!isMobile && (activeTab === TIMELINE_TAB.all || activeTab === TIMELINE_TAB.logistics) ? (
          <Button onClick={handleAddNoteClick} variant="contained" startIcon={<NoteIcon />} disabled={!vehicle?.id}>
            Add notes
          </Button>
        ) : null}
        {!isMobile ? <Box sx={{ flex: 1 }} /> : null}
        {!isMobile ? (
          <Button onClick={onClose} variant="outlined">
            Close
          </Button>
        ) : (
          <>
            <Button onClick={onClose} variant="outlined">
              Close
            </Button>
            {(activeTab === TIMELINE_TAB.all || activeTab === TIMELINE_TAB.logistics) && (
              <Button onClick={handleAddNoteClick} variant="contained" startIcon={<NoteIcon />} disabled={!vehicle?.id}>
                Add notes
              </Button>
            )}
          </>
        )}
      </DialogActions>

      <ConfirmDialog
        open={markDeliveredConfirmOpen}
        title="Mark delivered to customer?"
        message="This will add a delivered status to the timeline. Transport SLA steps will treat the vehicle as delivered as of this time."
        confirmText="Mark delivered"
        cancelText="Cancel"
        onConfirm={handleConfirmMarkDelivered}
        onCancel={() => setMarkDeliveredConfirmOpen(false)}
        severity="info"
      />
      <ConfirmDialog
        open={unmarkDeliveredConfirmOpen}
        title="Remove manual delivery mark?"
        message="This removes the manual delivery entry from the timeline. It cannot be undone except by marking delivered again."
        confirmText="Unmark"
        cancelText="Cancel"
        onConfirm={handleConfirmUnmarkDelivered}
        onCancel={() => setUnmarkDeliveredConfirmOpen(false)}
        severity="warning"
      />

      {/* Add Note Dialog */}
      <Dialog
        open={addNoteDialogOpen}
        onClose={() => {
          if (isSavingNote || isAddingRepairTruckNote) return
          setNewNoteFiles([])
          setAddNoteDialogOpen(false)
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add note</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Note type
          </Typography>
          <ToggleButtonGroup
            value={newNoteKind}
            exclusive
            fullWidth
            size="small"
            onChange={(_, value: 'generic' | 'repair' | null) => {
              if (!value) return
              if (value === 'repair' && !isAtRepairShop) return
              setNewNoteKind(value)
              if (value !== 'repair') {
                setNewNoteMarkImagesAsDamage(false)
                setNewNoteRepairPdfKind('invoice')
              }
            }}
            sx={{
              mb: 2,
              // Force every direct child (incl. tooltip span wrapper) to take equal width.
              '& > *': { flex: '1 1 0', minWidth: 0 },
              '& > span > .MuiToggleButton-root': { width: '100%' },
            }}
          >
            <ToggleButton value="generic" sx={{ textTransform: 'none' }}>
              <NoteIcon fontSize="small" sx={{ mr: 0.75 }} />
              Generic
            </ToggleButton>
            <Tooltip
              title={
                isAtRepairShop
                  ? 'Adds a repair shop note (right side of timeline)'
                  : 'Truck is not currently at a repair shop'
              }
              placement="top"
            >
              {/* span wrapper so Tooltip can render on disabled ToggleButton */}
              <span style={{ display: 'flex' }}>
                <ToggleButton
                  value="repair"
                  disabled={!isAtRepairShop}
                  sx={{ textTransform: 'none' }}
                >
                  <BuildIcon fontSize="small" sx={{ mr: 0.75 }} />
                  Repair
                </ToggleButton>
              </span>
            </Tooltip>
          </ToggleButtonGroup>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            {newNoteKind === 'repair'
              ? 'Repair notes appear on the right side of the timeline alongside repair shop activity. Attachments (photos, videos, PDFs) are stored on the repair timeline like shop uploads.'
              : 'Generic notes appear on the timeline as standard notes. You can attach files. Customer CRM activity is logged from the Customer tab.'}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            placeholder={
              newNoteKind === 'repair' ? 'Enter repair note...' : 'Enter note...'
            }
            value={newNoteText}
            onChange={(e) => setNewNoteText(e.target.value)}
            sx={{ mt: 1 }}
          />
          <input
            ref={addNoteFileInputRef}
            type="file"
            multiple
            hidden
            accept={
              newNoteKind === 'repair'
                ? 'image/*,video/*,application/pdf,.pdf'
                : undefined
            }
            onChange={(e) => {
              const list = e.target.files
              if (!list?.length) return
              setNewNoteFiles((prev) => [...prev, ...Array.from(list)].slice(0, 20))
              e.target.value = ''
            }}
          />
          <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
            <Button
              type="button"
              size="small"
              variant="outlined"
              startIcon={<AttachFileIcon />}
              onClick={() => addNoteFileInputRef.current?.click()}
              disabled={isSavingNote || isAddingRepairTruckNote || newNoteFiles.length >= 20}
            >
              Add attachments
            </Button>
            <Typography variant="caption" color="text.secondary">
              {newNoteKind === 'repair'
                ? 'Images, videos, or PDFs (up to 20); same storage as repair shop uploads.'
                : 'Up to 20 files; stored in Firebase Storage.'}
            </Typography>
          </Box>
          {newNoteKind === 'repair' && newNoteFiles.some((f) => f.type.startsWith('image/')) ? (
            <FormControlLabel
              sx={{ mt: 1, display: 'flex' }}
              control={
                <Checkbox
                  size="small"
                  checked={newNoteMarkImagesAsDamage}
                  onChange={(e) => setNewNoteMarkImagesAsDamage(e.target.checked)}
                  disabled={isSavingNote || isAddingRepairTruckNote}
                />
              }
              label={
                <Typography variant="body2" color="text.secondary">
                  Mark images as damage photos
                </Typography>
              }
            />
          ) : null}
          {newNoteKind === 'repair' &&
          newNoteFiles.some(
            (f) => f.type === 'application/pdf' || /\.pdf$/i.test(f.name)
          ) ? (
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                PDF attachments
              </Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={newNoteRepairPdfKind}
                onChange={(_, value: RepairPdfMediaKind | null) => {
                  if (value) setNewNoteRepairPdfKind(value)
                }}
                disabled={isSavingNote || isAddingRepairTruckNote}
              >
                <ToggleButton value="invoice" sx={{ textTransform: 'none' }}>
                  Invoice
                </ToggleButton>
                <ToggleButton value="estimate" sx={{ textTransform: 'none' }}>
                  Estimate
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          ) : null}
          {newNoteFiles.length > 0 ? (
            <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {newNoteFiles.map((file, fileIndex) => (
                <Chip
                  key={`${file.name}-${file.size}-${fileIndex}`}
                  label={file.name}
                  size="small"
                  onDelete={() =>
                    setNewNoteFiles((prev) => prev.filter((_, chipIndex) => chipIndex !== fileIndex))
                  }
                />
              ))}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => {
              resetAddNoteDialogState()
              setAddNoteDialogOpen(false)
            }}
            variant="outlined"
            disabled={isSavingNote || isAddingRepairTruckNote}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveNote}
            variant="contained"
            disabled={
              isSavingNote ||
              isAddingRepairTruckNote ||
              (newNoteKind === 'repair'
                ? (!newNoteText.trim() && newNoteFiles.length === 0) || !isAtRepairShop
                : !newNoteText.trim() && newNoteFiles.length === 0)
            }
          >
            {isSavingNote || isAddingRepairTruckNote ? 'Saving...' : 'Save note'}
          </Button>
        </DialogActions>
      </Dialog>
      <MediaGalleryDialog
        open={Boolean(mediaGalleryOpen)}
        onClose={() => setMediaGalleryOpen(null)}
        photos={mediaGalleryOpen?.photos ?? []}
        videos={mediaGalleryOpen?.videos ?? []}
      />
      {inspectionChecklistViewRepairTruck && (
        <InspectionChecklistAnswersDialog
          open
          onClose={() => setInspectionChecklistViewRepairTruck(null)}
          repairTruck={inspectionChecklistViewRepairTruck}
          completeChecklistId={inspectionChecklistDialogIds.completeChecklistId}
          intakeChecklistId={inspectionChecklistDialogIds.intakeChecklistId}
          initialTab="complete"
        />
      )}
    </AdminDialog>
  )
}

export default TimelineDialog
